const cartFetchInit = {
  cache: 'no-store',
  credentials: 'same-origin',
};

const CartActionsV2 = {
  async add(payload, options = {}) {
    const response = await fetch(this.cartAddUrl, {
      method: 'POST',
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: payload,
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add item to cart.');
    }

    if (options.dispatch !== false) {
      this.dispatchCartAdd(data, options);
    }

    return data;
  },

  async addItem(item, options = {}) {
    return this.addItems([item], options);
  },

  async addItems(items, options = {}) {
    const response = await fetch(this.cartAddUrl, {
      method: 'POST',
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add item to cart.');
    }

    if (options.dispatch !== false) {
      this.dispatchCartAdd(data, options);
    }

    return data;
  },

  async change(id, quantity, options = {}) {
    const body = {
      id,
      quantity,
    };

    if (Object.prototype.hasOwnProperty.call(options, 'sellingPlan')) {
      body.selling_plan = options.sellingPlan || '';
    }

    const response = await fetch(this.cartChangeUrl, {
      method: 'POST',
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update cart item.');
    }

    if (options.dispatch !== false) {
      document.dispatchEvent(
        new CustomEvent('cart-drawer-v2:updated', {
          bubbles: true,
          detail: {
            cart: data,
          },
        }),
      );
      dispatchHeaderCartCount(data);
    }

    logCartDiscountDebug('cart.js change', data);
    return data;
  },

  async get() {
    const response = await fetch(this.cartUrl, {
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to fetch cart.');
    }

    logCartDiscountDebug('cart.js get', data);
    return data;
  },

  async updateDiscounts(discountCodes = []) {
    const codes = Array.isArray(discountCodes) ? discountCodes : [discountCodes];
    const response = await fetch(this.cartUpdateUrl, {
      method: 'POST',
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: codes.filter(Boolean).join(','),
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update cart discounts.');
    }

    document.dispatchEvent(
      new CustomEvent('cart-drawer-v2:updated', {
        bubbles: true,
        detail: {
          cart: data,
        },
      }),
    );

    logCartDiscountDebug('cart.js updateDiscounts', data);
    return data;
  },

  async updateQuantities(updates, options = {}) {
    const response = await fetch(this.cartUpdateUrl, {
      method: 'POST',
      ...cartFetchInit,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        updates,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to update cart items.');
    }

    if (options.dispatch !== false) {
      document.dispatchEvent(
        new CustomEvent('cart-drawer-v2:updated', {
          bubbles: true,
          detail: {
            cart: data,
          },
        }),
      );
      dispatchHeaderCartCount(data);
    }

    return data;
  },

  dispatchCartAdd(data, options = {}) {
    const event = new CustomEvent('cart-drawer-v2:add', {
      bubbles: true,
      detail: {
        item: data,
        button: options.button,
        openDrawer: options.openDrawer !== false,
      },
    });

    if (options.source instanceof HTMLElement) {
      options.source.dispatchEvent(event);
      return;
    }

    document.dispatchEvent(event);
  },

  get cartAddUrl() {
    if (window.theme?.routes?.cart_add_url) return window.theme.routes.cart_add_url;
    if (window.routes?.cart_add_url) return window.routes.cart_add_url;

    return `${this.rootUrl}cart/add.js`;
  },

  get cartChangeUrl() {
    if (window.theme?.routes?.cart_change_url) return window.theme.routes.cart_change_url;
    if (window.routes?.cart_change_url) return window.routes.cart_change_url;

    return `${this.rootUrl}cart/change.js`;
  },

  get cartUrl() {
    if (window.theme?.routes?.cart_url) return `${window.theme.routes.cart_url}.js`;
    if (window.routes?.cart_url) return `${window.routes.cart_url}.js`;

    return `${this.rootUrl}cart.js`;
  },

  get cartUpdateUrl() {
    if (window.theme?.routes?.cart_update_url) return window.theme.routes.cart_update_url;
    if (window.routes?.cart_update_url) return window.routes.cart_update_url;

    return `${this.rootUrl}cart/update.js`;
  },

  get rootUrl() {
    return window.Shopify?.routes?.root || '/';
  },
};

window.CartActionsV2 = {
  ...(window.CartActionsV2 || {}),
  ...CartActionsV2,
};

function getPromoDiscountPercentFromDrawer() {
  const drawer = document.querySelector('cart-drawer-v2[data-promo-discount-percent]');
  const percent = Number.parseInt(drawer?.dataset?.promoDiscountPercent || '30', 10);
  return Number.isFinite(percent) && percent > 0 ? percent : 0;
}

function isCartItem90DaySubscription(item) {
  const planName = item.selling_plan_allocation?.selling_plan?.name || item.selling_plan || '';
  const title = item.title || item.product_title || '';
  const variantTitle = item.variant_title || '';

  return (
    /90/i.test(planName) ||
    /90\s*day/i.test(title) ||
    /90\s*day/i.test(variantTitle) ||
    /90\s*day/i.test(planName)
  );
}

function getCartItemPromoPricing(item, promoPercent) {
  if (!item?.selling_plan_allocation || promoPercent <= 0 || !isCartItem90DaySubscription(item)) {
    return null;
  }

  const quantity = Number(item.quantity) || 1;
  const baseUnit =
    Number(item.original_price) ||
    Number(item.selling_plan_allocation?.price) ||
    Number(item.price) ||
    0;
  const baseLine = baseUnit * quantity;
  const displayLine = Math.round((baseLine * (100 - promoPercent)) / 100);
  const shopifyFinal = Number(item.final_line_price) || 0;
  const hasShopifyDiscount =
    (item.line_level_discount_allocations || []).some((allocation) => Number(allocation?.amount) > 0) ||
    (item.discounts || []).some((discount) => Number(discount?.amount) > 0);

  return {
    promoPercent,
    baseUnit,
    baseLine,
    displayLine,
    shopifyFinal,
    hasShopifyDiscount,
    themeWouldShowBadge: !hasShopifyDiscount && baseLine > 0,
    themeWouldShowCompare: baseLine > displayLine,
    themeDisplayFinal: displayLine > 0 ? displayLine : shopifyFinal,
  };
}

let supplyUpgradeDebugActive = false;

function summarizeCartItemsForLog(cart) {
  return (cart?.items || []).map((item) => ({
    key: item.key,
    variant_id: item.variant_id || item.id,
    title: item.variant_title || item.title,
    quantity: item.quantity,
    selling_plan_id:
      item.selling_plan_allocation?.selling_plan?.id
      || item.selling_plan_allocation?.selling_plan_id
      || item.selling_plan
      || null,
    selling_plan_name: item.selling_plan_allocation?.selling_plan?.name || null,
    options: item.variant_options || item.options_with_values?.map((option) => option.value),
  }));
}

const CART_DRAWER_V2_BUILD = '2026-09-10-subscription-upgrade-v11';

function logSupplyUpgrade(step, details = {}) {
  console.log(`[cart-drawer-v2] supply-upgrade → ${step}`, details);
}

function resolveSupplyUpgradeButton(target) {
  if (!(target instanceof Element)) return null;

  const direct = target.closest(
    '[data-cart-drawer-v2-supply-upgrade], .cart-drawer-v2__supply-upgrade-button',
  );
  if (direct) return direct;

  const banner = target.closest('.cart-drawer-v2__supply-upgrade');
  if (!banner) return null;

  return banner.querySelector(
    '[data-cart-drawer-v2-supply-upgrade], .cart-drawer-v2__supply-upgrade-button',
  );
}

function triggerSupplyUpgrade(drawer, trigger, via = 'unknown') {
  if (!drawer || !trigger) {
    logSupplyUpgrade('click missed', { reason: 'missing drawer or trigger', via });
    return false;
  }

  if (typeof drawer.upgradeLineSupply !== 'function') {
    logSupplyUpgrade('click missed', { reason: 'upgradeLineSupply unavailable', via });
    return false;
  }

  if (
    trigger.getAttribute('aria-disabled') === 'true'
    || trigger.dataset.supplyUpgradeProcessing === 'true'
  ) {
    logSupplyUpgrade('click blocked', {
      via,
      ariaDisabled: trigger.getAttribute('aria-disabled'),
      processing: trigger.dataset.supplyUpgradeProcessing === 'true',
    });
    return false;
  }

  logSupplyUpgrade('button click', {
    via,
    targetVariantId: trigger.dataset.targetVariantId || null,
    targetSellingPlanId: trigger.dataset.targetSellingPlanId || null,
    syncingBonuses: drawer.classList.contains('is-syncing-bonuses'),
  });

  void drawer.upgradeLineSupply(trigger);
  return true;
}

function onSupplyUpgradeDocumentClick(event) {
  const trigger = resolveSupplyUpgradeButton(event.target);
  if (!trigger) return;

  const drawer = trigger.closest('cart-drawer-v2');
  if (!triggerSupplyUpgrade(drawer, trigger, 'global')) return;

  event.preventDefault();
  event.stopPropagation();
}

if (!window.__cartDrawerV2SupplyUpgradeBound) {
  window.__cartDrawerV2SupplyUpgradeBound = true;
  document.addEventListener('click', onSupplyUpgradeDocumentClick, true);
  logSupplyUpgrade('global listener bound', { build: CART_DRAWER_V2_BUILD });
}

window.__debugCartDrawerSupplyUpgrade = () => {
  const button = document.querySelector(
    '[data-cart-drawer-v2-supply-upgrade], .cart-drawer-v2__supply-upgrade-button',
  );
  const drawer = button?.closest('cart-drawer-v2');

  return {
    build: window.__cartDrawerV2Build,
    button,
    drawer,
    disabled: button?.disabled ?? null,
    ariaDisabled: button?.getAttribute('aria-disabled'),
    syncingBonuses: drawer?.classList.contains('is-syncing-bonuses') ?? null,
    dataset: button ? { ...button.dataset } : null,
  };
};

function logSupplyUpgradeApi(action, details = {}) {
  console.log(`[cart-drawer-v2] supply-upgrade API → ${action}`, details);
}

function logSupplyUpgradeError(action, error, details = {}) {
  console.error(`[cart-drawer-v2] supply-upgrade ERROR → ${action}`, {
    ...details,
    message: error?.message || String(error),
  });
}

function logCartDiscountDebug(source, cart) {
  if (supplyUpgradeDebugActive && (source === 'cart.js get' || source === 'cart.js change')) {
    return;
  }
  const promoPercent = getPromoDiscountPercentFromDrawer();
  const items = (cart?.items || []).map((item) => ({
    title: item.title,
    key: item.key,
    original_line_price: item.original_line_price,
    final_line_price: item.final_line_price,
    line_price: item.line_price,
    discounts: item.discounts,
    discount_titles: (item.discounts || []).map((discount) => discount?.title),
    line_level_discount_allocations: item.line_level_discount_allocations,
    allocation_types: (item.line_level_discount_allocations || []).map((allocation) => ({
      amount: allocation?.amount,
      type: allocation?.discount_application?.type,
      title: allocation?.discount_application?.title,
      code: allocation?.discount_application?.code,
    })),
    discount_allocations: item.discount_allocations,
    selling_plan: item.selling_plan_allocation?.selling_plan?.name || item.selling_plan_name || null,
    is_90_day_subscription: isCartItem90DaySubscription(item),
    theme_promo: getCartItemPromoPricing(item, promoPercent),
  }));

  console.groupCollapsed(`[cart-drawer-v2] discounts: ${source}`);
  console.log('totals', {
    total_price: cart?.total_price,
    original_total_price: cart?.original_total_price,
    total_discount: cart?.total_discount,
    item_count: cart?.item_count,
  });
  console.log('cart.discount_applications', cart?.discount_applications);
  console.log('cart.cart_level_discount_applications', cart?.cart_level_discount_applications);
  console.log('cart.discount_codes', cart?.discount_codes);
  console.table(items);
  console.log('items detail', items);
  console.log('promo note', {
    promo_discount_percent: promoPercent,
    message:
      'Theme promo % adjusts PDP + cart display. Real checkout discount requires a matching Shopify automatic discount.',
    shopify_line_discounts_found: items.some(
      (item) => (item.allocation_types || []).length > 0 || (item.discount_titles || []).length > 0,
    ),
    items_without_shopify_discount: items.filter(
      (item) => !(item.allocation_types || []).length && !(item.discount_titles || []).length,
    ),
    items_with_theme_promo: items.filter((item) => item.theme_promo?.themeWouldShowBadge),
  });
  console.groupEnd();
}

function dispatchHeaderCartCount(cartOrCount) {
  const cartCount = typeof cartOrCount === 'number' ? cartOrCount : cartOrCount?.item_count;

  if (cartCount == null) return;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      bubbles: true,
      detail: {
        cartCount,
      },
    }),
  );
}

class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentChange = this.onDocumentChange.bind(this);
    this.onDocumentInput = this.onDocumentInput.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onCartAdd = this.onCartAdd.bind(this);
    this.lastTrigger = null;
    this.discountStorageKey = 'cart-drawer-v2-discount-code';
    this.discountCelebrationTimer = null;
  }

  connectedCallback() {
    console.log('[cart-drawer-v2] connected', {
      build: window.__cartDrawerV2Build || 'unknown',
    });

    this.bindElements();

    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('input', this.onDocumentInput);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('cart-drawer-v2:add', this.onCartAdd);
    document.addEventListener('theme:product:added', this.onCartAdd);

    this.close(false);
    this.logAjaxDiscountDebug();
  }

  logLiquidDiscountDebug() {
    const liquidNode = this.querySelector('[data-cart-drawer-v2-discount-debug]');
    if (!liquidNode?.textContent) return;

    try {
      logCartDiscountDebug('liquid section', JSON.parse(liquidNode.textContent));
    } catch (error) {
      console.warn('[cart-drawer-v2] unable to parse liquid discount debug JSON', error, liquidNode.textContent);
    }
  }

  logAjaxDiscountDebug() {
    if (!window.CartActionsV2?.get) return;

    window.CartActionsV2.get()
      .then((cart) => {
        logCartDiscountDebug('cart drawer connected', cart);
        this.syncDiscountCodesFromCart(cart);
      })
      .catch((error) => {
        console.warn('[cart-drawer-v2] unable to fetch cart.js for discount debug', error);
      });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('change', this.onDocumentChange);
    document.removeEventListener('input', this.onDocumentInput);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('cart-drawer-v2:add', this.onCartAdd);
    document.removeEventListener('theme:product:added', this.onCartAdd);
  }

  bindElements() {
    this.panel = this.querySelector('[data-cart-drawer-v2-panel]');
    this.policyCheckbox = this.querySelector('[data-cart-drawer-v2-policy-checkbox-input]');
    this.checkoutButton = this.querySelector('[data-cart-drawer-v2-checkout]');
    this.bindSupplyUpgradeButtons();
    this.hydrateDiscountField();
    this.hydrateDiscountList(this.getSavedDiscountCodes());
    this.syncPolicyCheckoutState();
    this.logLiquidDiscountDebug();
    this.lockSubscriptionBonusItems();
  }

  getLinePropertyValue(item, key) {
    if (window.SubscriptionBonusProducts?.getLineProperty) {
      return window.SubscriptionBonusProducts.getLineProperty(item, key);
    }

    const properties = item?.properties;
    if (!properties) return '';

    if (typeof properties === 'object' && !Array.isArray(properties)) {
      return properties[key] == null ? '' : String(properties[key]);
    }

    return '';
  }

  lockBonusItemElement(itemEl) {
    if (!(itemEl instanceof HTMLElement)) return;

    itemEl.classList.add('cart-drawer-v2-item--bonus');
    itemEl.setAttribute('data-subscription-bonus', 'true');

    itemEl.querySelectorAll('.cart-drawer-v2-item__remove, .cart-drawer-v2-item__quantity').forEach((el) => {
      el.remove();
    });

    itemEl.querySelectorAll('a.cart-drawer-v2-item__media, .cart-drawer-v2-item__title a').forEach((link) => {
      const replacement = document.createElement(link.classList.contains('cart-drawer-v2-item__media') ? 'div' : 'span');
      replacement.className = link.className;
      if (link.getAttribute('aria-label')) {
        replacement.setAttribute('aria-label', link.getAttribute('aria-label'));
      }
      replacement.innerHTML = link.innerHTML;
      link.replaceWith(replacement);
    });

    this.applyBonusFreePrice(itemEl);
  }

  applyBonusFreePrice(itemEl) {
    const priceEl = itemEl.querySelector('.cart-drawer-v2-item__price');
    if (!priceEl) return;

    const purchase = itemEl.querySelector('.cart-drawer-v2-item__purchase');
    if (purchase && !purchase.contains(priceEl)) {
      purchase.appendChild(priceEl);
    }

    priceEl.classList.add('cart-drawer-v2-item__price--free');

    let compareEl = priceEl.querySelector('.cart-drawer-v2-item__price-compare');
    const currentEl = priceEl.querySelector('.cart-drawer-v2-item__price-current');
    const currentText = (currentEl?.textContent || '').replace(/\s+/g, ' ').trim();
    const isAlreadyFree = /^free$/i.test(currentText);
    const titleText = (itemEl.querySelector('.cart-drawer-v2-item__title')?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    const isFreeShipping = titleText === 'free shipping';

    if (!compareEl) {
      compareEl = document.createElement('s');
      compareEl.className = 'cart-drawer-v2-item__price-compare';
      priceEl.prepend(compareEl);
    }

    compareEl.classList.remove('ab-test-compare-price');

    if (!isAlreadyFree && currentText && !/^\$?0([.,]0+)?$/.test(currentText)) {
      compareEl.textContent = currentText;
    } else if (isFreeShipping && !compareEl.textContent.trim()) {
      compareEl.textContent = this.formatCartMoney(799);
    }

    if (currentEl) {
      currentEl.textContent = 'FREE';
    }
  }

  async lockSubscriptionBonusItems() {
    if (window.SubscriptionBonusProducts && !window.SubscriptionBonusProducts.isEnabled()) {
      return;
    }

    const alreadyMarked = this.querySelectorAll('[data-subscription-bonus="true"], .cart-drawer-v2-item--bonus');
    alreadyMarked.forEach((itemEl) => this.lockBonusItemElement(itemEl));

    if (!window.CartActionsV2?.get) return;

    try {
      const cart = await window.CartActionsV2.get();
      const bonusKeys = new Set(
        (cart?.items || [])
          .filter((item) => Boolean(this.getLinePropertyValue(item, '_subscription_bonus_for')))
          .map((item) => item.key),
      );

      if (!bonusKeys.size) return;

      this.querySelectorAll('[data-cart-drawer-v2-item]').forEach((itemEl) => {
        if (!bonusKeys.has(itemEl.dataset.lineKey)) return;
        this.lockBonusItemElement(itemEl);
      });
    } catch (error) {
      // Ignore cart lookup errors for bonus locking.
    }
  }

  onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const closeTrigger = event.target.closest('[data-cart-drawer-v2-close]');
    if (closeTrigger) {
      event.preventDefault();
      this.close();
      return;
    }

    const openTrigger = event.target.closest('[data-cart-toggle]');
    if (openTrigger) {
      event.preventDefault();
      this.open(openTrigger);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-drawer-v2-remove]');
    if (removeTrigger) {
      event.preventDefault();
      this.removeLineItem(removeTrigger);
      return;
    }

    const quantityTrigger = event.target.closest('[data-cart-drawer-v2-quantity-change]');
    if (quantityTrigger) {
      event.preventDefault();
      this.changeLineQuantity(quantityTrigger);
      return;
    }

    const subscriptionUpgradeTrigger = event.target.closest('[data-cart-drawer-v2-subscription-upgrade]');
    if (subscriptionUpgradeTrigger) {
      event.preventDefault();

      if (typeof this.upgradeLineToSubscription === 'function') {
        this.upgradeLineToSubscription(subscriptionUpgradeTrigger);
      } else {
        this.changeLineSellingPlan(
          subscriptionUpgradeTrigger,
          subscriptionUpgradeTrigger.dataset.cartSellingPlanId || '',
        );
      }

      return;
    }

    const supplyUpgradeTrigger = resolveSupplyUpgradeButton(event.target);
    if (supplyUpgradeTrigger && this.contains(supplyUpgradeTrigger)) {
      triggerSupplyUpgrade(this, supplyUpgradeTrigger, 'document');
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const discountApplyTrigger = event.target.closest('[data-cart-drawer-v2-discount-apply]');
    if (discountApplyTrigger) {
      event.preventDefault();
      this.applyDiscountCode(discountApplyTrigger);
      return;
    }

    const discountRemoveTrigger = event.target.closest('[data-cart-drawer-v2-discount-remove]');
    if (discountRemoveTrigger) {
      event.preventDefault();
      this.removeDiscountCode(discountRemoveTrigger);
      return;
    }

    const checkoutTrigger = event.target.closest('[data-cart-drawer-v2-checkout]');
    if (checkoutTrigger && this.contains(checkoutTrigger)) {
      if (checkoutTrigger.disabled) return;

      event.preventDefault();
      this.submitCheckout(checkoutTrigger);
      return;
    }

    const addToCartButton = event.target.closest('[data-add-to-cart]');
    if (addToCartButton && addToCartButton.closest('[data-product-form]')) {
      if (addToCartButton.disabled) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addProductForm(addToCartButton);
    }
  }

  onDocumentChange(event) {
    if (!(event.target instanceof Element)) return;

    const policyCheckbox = event.target.closest('[data-cart-drawer-v2-policy-checkbox-input]');
    if (policyCheckbox && this.contains(policyCheckbox)) {
      this.syncPolicyCheckoutState();
      return;
    }

    const sellingPlanSelect = event.target.closest('[data-cart-drawer-v2-selling-plan-select]');
    if (!sellingPlanSelect) return;

    this.changeLineSellingPlan(sellingPlanSelect, sellingPlanSelect.value);
  }

  onDocumentInput(event) {
    if (!(event.target instanceof Element)) return;

    const discountInput = event.target.closest('[data-cart-drawer-v2-discount-input]');
    if (!discountInput || !this.contains(discountInput)) return;

    const form = discountInput.closest('[data-cart-drawer-v2-form]');
    this.clearDiscountMessages(form);
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  async onCartAdd(event) {
    const shouldOpenDrawer = event.detail?.openDrawer !== false;
    const trigger = event.detail?.button || event.target;
    const wasOpen = this.classList.contains('is-open');

    await this.refresh();
    await this.syncDiscountedPricesFromCart();

    if (!shouldOpenDrawer) {
      if (wasOpen) {
        this.open(trigger);
      }

      return;
    }

    this.open(trigger);
  }

  open(trigger = null) {
    if (!this.panel) return;

    this.lastTrigger = trigger || document.activeElement;
    this.classList.remove('is-closing');
    this.classList.remove('is-syncing-bonuses');
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('aria-busy', 'false');
    document.body.classList.remove('subscription-bonus-syncing');
    this.resetSupplyUpgradeButtons();
    document.body.classList.add('cart-drawer-v2-open');

    requestAnimationFrame(() => {
      this.panel?.focus({ preventScroll: true });
    });
  }

  close(animate = true) {
    if (!animate) {
      this.classList.add('is-not-animated');
    }

    if (animate && this.classList.contains('is-open')) {
      this.classList.add('is-closing');
    }

    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-v2-open');

    if (animate && this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus({ preventScroll: true });
    }

    if (!animate) {
      requestAnimationFrame(() => {
        this.classList.remove('is-not-animated');
      });
      return;
    }

    window.setTimeout(() => {
      this.classList.remove('is-closing');
    }, 280);
  }

  async addProductForm(button) {
    const form = button.closest('form');

    if (!form) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    button.classList.add('is-loading');
    button.classList.remove('is-added');
    button.disabled = true;

    const payload = new FormData(form);
    const shouldSyncBonuses = window.SubscriptionBonusProducts?.shouldAttachBonuses?.(form, payload) === true;

    const subscriptionAddItems = shouldSyncBonuses && window.SubscriptionBonusProducts
      ? window.SubscriptionBonusProducts.buildSubscriptionAddItems(form, payload)
      : null;

    if (shouldSyncBonuses && window.SubscriptionBonusProducts && !subscriptionAddItems) {
      window.SubscriptionBonusProducts.registerLoadingTarget(button);
    }

    try {
      let addResult;

      if (subscriptionAddItems) {
        addResult = await window.CartActionsV2.addItems(subscriptionAddItems, {
          button,
          source: button,
          dispatch: false,
        });
      } else {
        addResult = await window.CartActionsV2.add(payload, {
          button,
          source: button,
          dispatch: false,
        });

        if (shouldSyncBonuses && window.SubscriptionBonusProducts) {
          let cart = await window.CartActionsV2.get();
          cart = await window.SubscriptionBonusProducts.syncCart(cart);
        }
      }

      await this.refresh();

      window.CartActionsV2.dispatchCartAdd(addResult, {
        button,
        source: button,
      });

      return addResult;
    } catch (error) {
      console.error('Unable to add item to cart', error);

      document.dispatchEvent(
        new CustomEvent('theme:product:add-error', {
          bubbles: true,
          detail: {
            response: error,
            button,
          },
        }),
      );
    } finally {
      button.classList.remove('is-loading');
      button.classList.remove('is-added');
      button.disabled = false;
    }
  }

  removeLineItem(trigger) {
    const lineKey = trigger?.dataset.cartLineKey;

    if (!lineKey || trigger.getAttribute('aria-disabled') === 'true') return;

    this.updateLineItem(lineKey, 0);
  }

  changeLineQuantity(trigger) {
    const lineKey = trigger?.dataset.cartLineKey;
    const currentQuantity = Number.parseInt(trigger?.dataset.cartLineQuantity || '0', 10);
    const direction = Number.parseInt(trigger?.dataset.cartQuantityDirection || '0', 10);
    const nextQuantity = currentQuantity + direction;

    if (!lineKey || trigger.getAttribute('aria-disabled') === 'true') return;
    if (!Number.isFinite(nextQuantity) || nextQuantity < 1) return;

    this.updateLineItem(lineKey, nextQuantity);
  }

  changeLineSellingPlan(trigger, sellingPlanId) {
    const lineKey = trigger?.dataset.cartLineKey;
    const currentQuantity = Number.parseInt(trigger?.dataset.cartLineQuantity || '1', 10);
    const quantity = Number.isFinite(currentQuantity) && currentQuantity > 0 ? currentQuantity : 1;

    if (!lineKey || trigger.getAttribute('aria-disabled') === 'true') return;

    logSupplyUpgradeApi('subscription plan select', {
      lineKey,
      quantity,
      sellingPlanId: sellingPlanId || null,
    });

    this.updateLineItem(lineKey, quantity, {
      sellingPlan: sellingPlanId || '',
    });
  }

  async upgradeLineToSubscription(trigger) {
    const lineKey = trigger?.dataset.cartLineKey || '';
    const currentQuantity = Number.parseInt(trigger?.dataset.cartLineQuantity || '1', 10);
    const quantity = Number.isFinite(currentQuantity) && currentQuantity > 0 ? currentQuantity : 1;
    const requestedPlanId = this.normalizeSupplyUpgradeId(trigger?.dataset.cartSellingPlanId || '');
    const candidatePlanIds = this.parseSupplyUpgradeIdList(trigger?.dataset.cartSellingPlanIds);
    const planIds = candidatePlanIds.length ? candidatePlanIds : (requestedPlanId ? [requestedPlanId] : []);
    const expectedVariantId = this.normalizeSupplyUpgradeId(trigger?.dataset.cartVariantId || '');

    if (!lineKey || trigger.getAttribute('aria-disabled') === 'true') return;

    if (!planIds.length) {
      logSupplyUpgrade('subscription upgrade aborted', { reason: 'missing selling plan id' });
      this.changeLineSellingPlan(trigger, requestedPlanId);
      return;
    }

    this.setLineControlsDisabled(lineKey, true);
    this.setLineItemLoading(lineKey, true);

    try {
      let cart = await window.CartActionsV2.get();
      const sourceItem = (cart?.items || []).find((item) => item.key === lineKey);

      if (!sourceItem) {
        await this.refresh();
        return;
      }

      const sourceVariantId = this.normalizeSupplyUpgradeId(
        sourceItem.variant_id || sourceItem.id || expectedVariantId,
      );
      const properties = this.sanitizeSupplyUpgradeAddProperties(sourceItem.properties);
      const sourceRestoreItem = this.buildSupplyUpgradeCartItem(sourceVariantId, quantity, '');

      if (Object.keys(properties).length) {
        sourceRestoreItem.properties = properties;
      }

      logSupplyUpgradeApi('subscription upgrade (item button)', {
        lineKey,
        quantity,
        variantId: sourceVariantId,
        sellingPlanIds: planIds,
        sellingPlanName: trigger?.dataset.cartPlanName || null,
        supplyDays: trigger?.dataset.cartSupplyDays || null,
        planSource: trigger?.dataset.cartPlanSource || null,
      });

      const lineHasMatchingSubscription = (workingCart, sellingPlanId = '') => {
        return (workingCart?.items || []).find((item) => {
          if (this.normalizeSupplyUpgradeId(item.variant_id || item.id || '') !== sourceVariantId) {
            return false;
          }

          const itemPlanId = this.getCartItemSellingPlanId(item);
          if (!itemPlanId) return false;
          if (!sellingPlanId) return true;

          return itemPlanId === this.normalizeSupplyUpgradeId(sellingPlanId);
        }) || null;
      };

      let upgradedLine = null;

      for (const sellingPlanId of planIds) {
        try {
          cart = await window.CartActionsV2.change(lineKey, quantity, {
            dispatch: false,
            sellingPlan: sellingPlanId,
          });
        } catch (error) {
          logSupplyUpgradeError('subscription upgrade change failed', error, {
            sellingPlanId,
            lineKey,
          });
          cart = await window.CartActionsV2.get();
        }

        upgradedLine = lineHasMatchingSubscription(cart, sellingPlanId);
        if (upgradedLine) break;

        const remappedLine = (cart?.items || []).find((item) => {
          if (this.normalizeSupplyUpgradeId(item.variant_id || item.id || '') === sourceVariantId) {
            return false;
          }
          if (String(item.product_id || '') !== String(sourceItem.product_id || '')) return false;

          return Boolean(this.getCartItemSellingPlanId(item));
        });

        if (remappedLine) {
          logSupplyUpgrade('subscription upgrade remapped variant, restoring', {
            expectedVariantId: sourceVariantId,
            sellingPlanId,
            remappedVariantId: remappedLine.variant_id,
            remappedPlanId: this.getCartItemSellingPlanId(remappedLine),
          });

          cart = await this.removeSupplyUpgradeSourceLine(
            cart,
            remappedLine.key,
            this.getCartItemSellingPlanId(remappedLine),
          );
        }

        const addPayload = this.buildSupplyUpgradeCartItem(sourceVariantId, quantity, sellingPlanId);
        if (Object.keys(properties).length) {
          addPayload.properties = properties;
        }

        try {
          await window.CartActionsV2.addItem(addPayload, { dispatch: false });
        } catch (error) {
          logSupplyUpgradeError('subscription upgrade add failed', error, {
            sellingPlanId,
            sourceVariantId,
          });
          continue;
        }

        cart = await window.CartActionsV2.get();
        upgradedLine = lineHasMatchingSubscription(cart, sellingPlanId);
        if (upgradedLine) break;
      }

      if (!upgradedLine) {
        cart = await window.CartActionsV2.get();
        const sourceReturned = (cart?.items || []).some((item) => {
          return this.normalizeSupplyUpgradeId(item.variant_id || item.id || '') === sourceVariantId;
        });

        if (!sourceReturned) {
          await window.CartActionsV2.addItem(sourceRestoreItem, { dispatch: false });
        }

        throw new Error('Unable to attach a matching subscription without changing Days of Supply.');
      }

      cart = await window.CartActionsV2.get();
      const originalOneTime = (cart?.items || []).find((item) => {
        if (item.key === upgradedLine.key) return false;
        if (this.normalizeSupplyUpgradeId(item.variant_id || item.id || '') !== sourceVariantId) {
          return false;
        }

        return !this.getCartItemSellingPlanId(item);
      });

      if (originalOneTime) {
        cart = await this.removeSupplyUpgradeSourceLine(cart, originalOneTime.key, '');
      }

      if (window.SubscriptionBonusProducts?.syncCart) {
        cart = await window.SubscriptionBonusProducts.syncCart(cart, {
          showLoading: false,
          skipOrderFix: true,
        });
      }

      dispatchHeaderCartCount(cart);
      await this.refresh();
    } catch (error) {
      console.error('Unable to upgrade cart item to subscription', error);
      await this.refresh();
    } finally {
      this.setLineControlsDisabled(lineKey, false);
      this.setLineItemLoading(lineKey, false);
    }
  }

  async updateLineItem(lineKey, quantity, options = {}) {
    this.setLineControlsDisabled(lineKey, true);
    this.setLineItemLoading(lineKey, true);

    try {
      let cart = await window.CartActionsV2.get();
      const targetLine = (cart?.items || []).find((item) => item.key === lineKey);

      if (!targetLine) {
        // The rendered markup is older than the cart - a supply upgrade replaces the line it acts
        // on. Mutating a stale key would hit the wrong line, so re-render and let the customer
        // click the button that matches the cart they can actually see.
        console.warn('[cart-drawer-v2] cart line is out of date, refreshing instead of updating', {
          lineKey,
          quantity,
          options,
          cartItems: summarizeCartItemsForLog(cart),
        });
        await this.refresh();
        return;
      }

      const targetVariantId = String(targetLine.variant_id || targetLine.id || '');
      const relatedBonusKeys = quantity === 0
        ? (window.SubscriptionBonusProducts?.getBonusKeysForRemovedLine?.(cart, lineKey) || [])
        : [];

      if (relatedBonusKeys.length) {
        const updates = { [lineKey]: 0 };
        relatedBonusKeys.forEach((bonusKey) => {
          updates[bonusKey] = 0;
        });
        cart = await window.CartActionsV2.updateQuantities(updates, { dispatch: false });
      } else {
        cart = await window.CartActionsV2.change(lineKey, quantity, options);
      }

      // Attaching a selling plan must never change which variant is in the cart. If it does, the
      // plan did not belong to this variant and the line silently lost its supply length.
      if (quantity > 0 && Object.prototype.hasOwnProperty.call(options, 'sellingPlan')) {
        const preservedLine = (cart?.items || []).find(
          (item) => String(item.variant_id || item.id || '') === targetVariantId,
        );

        if (!preservedLine) {
          console.error('[cart-drawer-v2] selling plan change moved the line to another variant', {
            lineKey,
            expectedVariantId: targetVariantId,
            requestedSellingPlanId: options.sellingPlan || null,
            cartItems: summarizeCartItemsForLog(cart),
          });

          const remappedLine = (cart?.items || []).find((item) => {
            return String(item.product_id || '') === String(targetLine.product_id || '');
          });

          if (remappedLine) {
            cart = await window.CartActionsV2.change(remappedLine.key, 0, {
              dispatch: false,
              sellingPlan: this.getCartItemSellingPlanId(remappedLine) || '',
            });
          }

          const restorePayload = this.buildSupplyUpgradeCartItem(
            targetVariantId,
            quantity,
            options.sellingPlan,
          );
          const restoreProperties = this.sanitizeSupplyUpgradeAddProperties(targetLine.properties);

          if (Object.keys(restoreProperties).length) {
            restorePayload.properties = restoreProperties;
          }

          try {
            await window.CartActionsV2.addItem(restorePayload, { dispatch: false });
            cart = await window.CartActionsV2.get();

            const restoredLine = (cart?.items || []).find(
              (item) => String(item.variant_id || item.id || '') === targetVariantId,
            );

            if (!restoredLine) {
              const originalPayload = this.buildSupplyUpgradeCartItem(targetVariantId, quantity, '');
              if (Object.keys(restoreProperties).length) {
                originalPayload.properties = restoreProperties;
              }
              await window.CartActionsV2.addItem(originalPayload, { dispatch: false });
              cart = await window.CartActionsV2.get();
            }
          } catch (restoreError) {
            console.error('[cart-drawer-v2] unable to restore original variant after selling plan remap', restoreError);
          }
        }
      }

      if (window.SubscriptionBonusProducts?.syncCart) {
        cart = await window.SubscriptionBonusProducts.syncCart(cart);
      }

      await this.refresh();
    } catch (error) {
      console.error('Unable to update cart item', error);
      await this.refresh();
    } finally {
      this.setLineControlsDisabled(lineKey, false);
      this.setLineItemLoading(lineKey, false);
    }
  }

  normalizeSupplyUpgradeId(value) {
    const normalizedValue = String(value ?? '').trim();
    if (!normalizedValue) return '';

    const parsedValue = Number.parseInt(normalizedValue, 10);
    return Number.isFinite(parsedValue) ? String(parsedValue) : normalizedValue;
  }

  parseSupplyUpgradeIdList(value) {
    return [
      ...new Set(
        String(value ?? '')
          .split(',')
          .map((entry) => this.normalizeSupplyUpgradeId(entry))
          .filter(Boolean),
      ),
    ];
  }

  getSupplyUpgradePayload(trigger) {
    const triggerLineKey = trigger?.dataset.cartLineKey || '';

    return {
      sourceLineKey: triggerLineKey,
      sourceVariantId: this.normalizeSupplyUpgradeId(
        trigger?.dataset.sourceVariantId || String(triggerLineKey || '').split(':')[0] || '',
      ),
      sourceSellingPlanId: this.normalizeSupplyUpgradeId(trigger?.dataset.sourceSellingPlanId || ''),
      targetVariantId: this.normalizeSupplyUpgradeId(trigger?.dataset.targetVariantId || ''),
      targetSellingPlanId: this.normalizeSupplyUpgradeId(trigger?.dataset.targetSellingPlanId || ''),
      targetSellingPlanIds: this.parseSupplyUpgradeIdList(trigger?.dataset.targetSellingPlanIds),
      quantity: Number.parseInt(trigger?.dataset.cartLineQuantity || '1', 10),
    };
  }

  buildSupplyUpgradeCartItem(variantId, quantity, sellingPlanId = '') {
    const normalizedVariantId = Number.parseInt(this.normalizeSupplyUpgradeId(variantId), 10);
    const normalizedPlanId = Number.parseInt(this.normalizeSupplyUpgradeId(sellingPlanId), 10);
    const item = {
      id: Number.isFinite(normalizedVariantId) ? normalizedVariantId : variantId,
      quantity,
    };

    if (Number.isFinite(normalizedPlanId)) {
      item.selling_plan = normalizedPlanId;
    }

    return item;
  }

  buildSupplyUpgradeRestoreItem(sourceItem) {
    const restoreItem = this.buildSupplyUpgradeCartItem(
      sourceItem.variant_id || sourceItem.id || '',
      sourceItem.quantity,
      this.getCartItemSellingPlanId(sourceItem),
    );
    const properties = this.normalizeLineProperties(sourceItem.properties);

    if (Object.keys(properties).length > 0) {
      restoreItem.properties = properties;
    }

    restoreItem.product_id = sourceItem.product_id || '';

    return restoreItem;
  }

  async restoreSupplyUpgradeSourceLine(sourceRestoreItem) {
    if (!sourceRestoreItem?.id) return;

    const cart = await window.CartActionsV2.get();
    const restoreVariantId = String(sourceRestoreItem.id);
    const restorePlanId = this.normalizeSupplyUpgradeId(sourceRestoreItem.selling_plan || '');
    const alreadyExists = (cart?.items || []).some((item) => {
      if (String(item.variant_id || item.id || '') !== restoreVariantId) return false;
      if (!restorePlanId) return true;

      return this.getCartItemSellingPlanId(item) === restorePlanId;
    });

    if (alreadyExists) return;

    await window.CartActionsV2.addItem(sourceRestoreItem, { dispatch: false });
  }

  async addSupplyUpgradeTargetLine(targetVariantId, quantity, targetSellingPlanId = '', options = {}) {
    const normalizedTargetVariantId = this.normalizeSupplyUpgradeId(targetVariantId);
    const normalizedTargetPlanId = this.normalizeSupplyUpgradeId(targetSellingPlanId);
    const normalizedProperties = this.sanitizeSupplyUpgradeAddProperties(options.properties);
    const normalizedProductId = String(options.productId || '');
    const excludeLineKey = options.excludeLineKey || '';
    const lookupOptions = { productId: normalizedProductId };
    const planCandidates = [
      ...new Set(
        [normalizedTargetPlanId, ...(options.planCandidates || [])]
          .map((planId) => this.normalizeSupplyUpgradeId(planId))
          .filter(Boolean),
      ),
    ];

    const buildPayload = (planId) => {
      const payload = this.buildSupplyUpgradeCartItem(normalizedTargetVariantId, quantity, planId);

      if (Object.keys(normalizedProperties).length > 0) {
        payload.properties = normalizedProperties;
      }

      return payload;
    };

    const findLine = (cart, planId) =>
      this.findTargetUpgradeLine(cart, normalizedTargetVariantId, planId, excludeLineKey, lookupOptions);

    const addAndSettle = async (planId) => {
      const payload = buildPayload(planId);

      logSupplyUpgradeApi('cart/add.js', { withPlan: Boolean(planId), payload });

      const addResponse = await window.CartActionsV2.addItem(payload, { dispatch: false });

      logSupplyUpgradeApi('cart/add.js success', {
        withPlan: Boolean(planId),
        responseItems: summarizeCartItemsForLog(addResponse),
      });

      let line = findLine(addResponse, planId);

      for (let attempt = 0; attempt < 4 && !line; attempt += 1) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 150);
        });

        line = findLine(await window.CartActionsV2.get(), planId);
      }

      return line;
    };

    let lastError = null;
    // Set when an add succeeded but Shopify did not apply the requested plan: that line must be
    // repaired rather than added again, otherwise the variant ends up in the cart twice.
    let existingLine = null;

    // 1. Try every selling plan Liquid reported as allocated to the target variant. The first one
    //    is the plan the banner priced; the rest are fallbacks for when Shopify rejects it.
    for (const planId of planCandidates) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, 200);
          });
        }

        try {
          const line = await addAndSettle(planId);

          if (line) {
            logSupplyUpgrade('target line added', {
              key: line.key,
              variant_id: line.variant_id,
              requestedPlanId: normalizedTargetPlanId || null,
              appliedPlanId: this.getCartItemSellingPlanId(line),
              planFallback: planId !== normalizedTargetPlanId,
            });

            return line;
          }

          existingLine = findLine(await window.CartActionsV2.get(), '');
          lastError = new Error(
            `Supply upgrade target line missing after add (variant ${normalizedTargetVariantId}, plan ${planId}).`,
          );
          break;
        } catch (error) {
          lastError = error;
          logSupplyUpgradeError('cart/add.js with selling_plan failed', error, {
            variantId: normalizedTargetVariantId,
            sellingPlanId: planId,
            attempt: attempt + 1,
          });
        }
      }

      if (existingLine) break;
    }

    // 2. Last resort: get the variant into the cart without a plan, then attach one through
    //    cart/change.js.
    let plainLine = existingLine;

    if (!plainLine) {
      try {
        plainLine = await addAndSettle('');
      } catch (error) {
        lastError = error;
        logSupplyUpgradeError('cart/add.js without selling_plan failed', error, {
          variantId: normalizedTargetVariantId,
        });
      }
    }

    if (plainLine && !planCandidates.length) return plainLine;

    if (plainLine) {
      for (const planId of planCandidates) {
        logSupplyUpgradeApi('cart/change.js apply selling_plan', {
          lineKey: plainLine.key,
          quantity,
          sellingPlanId: planId,
          beforePlanId: this.getCartItemSellingPlanId(plainLine),
        });

        try {
          await window.CartActionsV2.change(plainLine.key, quantity, {
            dispatch: false,
            sellingPlan: planId,
          });
        } catch (error) {
          lastError = error;
          logSupplyUpgradeError('cart/change.js apply selling_plan failed', error, {
            lineKey: plainLine.key,
            sellingPlanId: planId,
          });
          continue;
        }

        const cartAfterChange = await window.CartActionsV2.get();
        const line = findLine(cartAfterChange, planId);

        if (line) {
          logSupplyUpgrade('selling_plan applied via change', {
            key: line.key,
            appliedPlanId: this.getCartItemSellingPlanId(line),
          });

          return line;
        }

        // Applying a selling plan rewrites the line key, so re-resolve before the next attempt.
        plainLine = findLine(cartAfterChange, '') || plainLine;
      }

      // The line may still have landed on a subscription plan of its own — keep it, the caller
      // verifies against the plan that actually stuck.
      const settledLine = findLine(await window.CartActionsV2.get(), '');

      if (settledLine && this.getCartItemSellingPlanId(settledLine)) {
        logSupplyUpgrade('target line kept with unexpected selling plan', {
          key: settledLine.key,
          requestedPlanId: normalizedTargetPlanId || null,
          appliedPlanId: this.getCartItemSellingPlanId(settledLine),
        });

        return settledLine;
      }

      // Never leave a one-time line behind when a subscription was requested.
      const lineToRollBack = settledLine || plainLine;

      try {
        await window.CartActionsV2.change(lineToRollBack.key, 0, { dispatch: false });
        logSupplyUpgrade('plan-less target line rolled back', { key: lineToRollBack.key });
      } catch (cleanupError) {
        logSupplyUpgradeError('unable to roll back plan-less target line', cleanupError, {
          lineKey: lineToRollBack.key,
        });
      }
    }

    throw (
      lastError
      || new Error(
        `Supply upgrade target line missing (variant ${normalizedTargetVariantId}, plan ${normalizedTargetPlanId}).`,
      )
    );
  }

  findActiveSourceLine(cart, options = {}) {
    const sourceLineKey = options.sourceLineKey || '';
    const sourceVariantId = String(options.sourceVariantId || '');
    const sourceSellingPlanId = this.normalizeSupplyUpgradeId(options.sourceSellingPlanId || '');
    const items = cart?.items || [];

    const strictMatch =
      items.find((item) => {
        if (!item?.quantity) return false;

        const itemVariantId = String(item.variant_id || item.id || '');
        const itemSellingPlanId = this.getCartItemSellingPlanId(item);

        if (sourceLineKey && item.key === sourceLineKey) {
          if (sourceVariantId && itemVariantId !== sourceVariantId) return false;
          if (sourceSellingPlanId && itemSellingPlanId !== sourceSellingPlanId) return false;

          return true;
        }

        if (!sourceVariantId || itemVariantId !== sourceVariantId) return false;
        if (sourceSellingPlanId && itemSellingPlanId !== sourceSellingPlanId) return false;

        return true;
      }) || null;

    if (strictMatch || !sourceVariantId) return strictMatch;

    const subscriptionLines = items.filter((item) => {
      if (!item?.quantity) return false;
      if (String(item.variant_id || item.id || '') !== sourceVariantId) return false;

      return Boolean(this.getCartItemSellingPlanId(item));
    });

    if (subscriptionLines.length === 1) {
      return subscriptionLines[0];
    }

    return null;
  }

  async removeSupplyUpgradeSourceLine(cart, sourceLineKey, sourceSellingPlanId = '') {
    const bonusKeys =
      window.SubscriptionBonusProducts?.getBonusKeysForRemovedLine?.(cart, sourceLineKey) || [];
    const normalizedSourcePlanId = this.normalizeSupplyUpgradeId(sourceSellingPlanId);
    const sourceLineIndex =
      (cart?.items || []).findIndex((item) => item.key === sourceLineKey) + 1;
    let workingCart = cart;

    for (const bonusKey of bonusKeys) {
      try {
        workingCart = await window.CartActionsV2.change(bonusKey, 0, { dispatch: false });
      } catch (error) {
        workingCart = await window.CartActionsV2.updateQuantities({ [bonusKey]: 0 }, { dispatch: false });
      }
    }

    const removeAttempts = [
      {
        label: 'change qty 0 with source selling_plan',
        run: () =>
          window.CartActionsV2.change(sourceLineKey, 0, {
            dispatch: false,
            sellingPlan: normalizedSourcePlanId,
          }),
      },
      {
        label: 'change qty 0 with empty selling_plan',
        run: () =>
          window.CartActionsV2.change(sourceLineKey, 0, {
            dispatch: false,
            sellingPlan: '',
          }),
      },
      {
        label: 'change qty 0',
        run: () => window.CartActionsV2.change(sourceLineKey, 0, { dispatch: false }),
      },
      {
        label: 'updateQuantities qty 0',
        run: () => window.CartActionsV2.updateQuantities({ [sourceLineKey]: 0 }, { dispatch: false }),
      },
      ...(sourceLineIndex > 0
        ? [
            {
              label: 'change qty 0 by line index with source selling_plan',
              run: () =>
                window.CartActionsV2.change(sourceLineIndex, 0, {
                  dispatch: false,
                  sellingPlan: normalizedSourcePlanId,
                }),
            },
            {
              label: 'change qty 0 by line index',
              run: () => window.CartActionsV2.change(sourceLineIndex, 0, { dispatch: false }),
            },
          ]
        : []),
    ];

    for (const attempt of removeAttempts) {
      logSupplyUpgradeApi('remove source attempt', {
        strategy: attempt.label,
        lineKey: sourceLineKey,
        sellingPlanId: normalizedSourcePlanId || null,
      });

      try {
        workingCart = await attempt.run();
      } catch (error) {
        logSupplyUpgradeError('remove source attempt failed', error, {
          strategy: attempt.label,
          lineKey: sourceLineKey,
        });
        continue;
      }

      const stillPresent = this.findActiveSourceLine(workingCart, {
        sourceLineKey,
        sourceSellingPlanId: normalizedSourcePlanId,
      });

      logSupplyUpgradeApi('remove source attempt result', {
        strategy: attempt.label,
        removed: !stillPresent,
        items: summarizeCartItemsForLog(workingCart),
      });

      if (!stillPresent) {
        return workingCart;
      }
    }

    logSupplyUpgrade('remove source exhausted', {
      lineKey: sourceLineKey,
      items: summarizeCartItemsForLog(workingCart),
    });

    return workingCart;
  }

  async removeAllSourceSubscriptionLines(options = {}) {
    const sourceVariantId = String(options.sourceVariantId || '');
    const sourceSellingPlanId = String(options.sourceSellingPlanId || '');
    let workingCart = options.cart || (await window.CartActionsV2.get());
    let attempts = 0;

    while (attempts < 5) {
      const activeLine = this.findActiveSourceLine(workingCart, {
        sourceLineKey: options.sourceLineKey || '',
        sourceVariantId,
        sourceSellingPlanId,
      });

      if (!activeLine) {
        return workingCart;
      }

      const activePlanId = this.getCartItemSellingPlanId(activeLine) || sourceSellingPlanId;
      workingCart = await this.removeSupplyUpgradeSourceLine(workingCart, activeLine.key, activePlanId);

      workingCart = await window.CartActionsV2.get();
      if (!this.findActiveSourceLine(workingCart, { sourceVariantId, sourceSellingPlanId })) {
        return workingCart;
      }

      attempts += 1;
    }

    return workingCart;
  }

  async removeAllLeftoverSourceLines(sourceVariantId, sourceSellingPlanId) {
    const normalizedSourceVariantId = String(sourceVariantId || '');
    const normalizedSourcePlanId = String(sourceSellingPlanId || '');
    let workingCart = await window.CartActionsV2.get();
    let attempts = 0;

    while (attempts < 5) {
      const activeLine = this.findActiveSourceLine(workingCart, {
        sourceVariantId: normalizedSourceVariantId,
        sourceSellingPlanId: normalizedSourcePlanId,
      });

      if (!activeLine) {
        return workingCart;
      }

      const activePlanId = this.getCartItemSellingPlanId(activeLine) || normalizedSourcePlanId;
      workingCart = await this.removeSupplyUpgradeSourceLine(workingCart, activeLine.key, activePlanId);
      workingCart = await window.CartActionsV2.get();

      if (!this.findActiveSourceLine(workingCart, {
        sourceVariantId: normalizedSourceVariantId,
        sourceSellingPlanId: normalizedSourcePlanId,
      })) {
        return workingCart;
      }

      attempts += 1;
    }

    throw new Error('Unable to remove leftover source subscription line after supply upgrade.');
  }

  async ensureSupplyUpgradeSourceRemoved(options = {}) {
    const workingCart = await this.removeAllSourceSubscriptionLines(options);

    if (
      this.findActiveSourceLine(workingCart, {
        sourceVariantId: options.sourceVariantId,
        sourceSellingPlanId: options.sourceSellingPlanId,
      })
    ) {
      throw new Error('Unable to remove source line before supply upgrade.');
    }

    return workingCart;
  }

  bindSupplyUpgradeButtons() {
    this.resetSupplyUpgradeButtons();

    const buttons = this.querySelectorAll(
      '[data-cart-drawer-v2-supply-upgrade], .cart-drawer-v2__supply-upgrade-button',
    );

    buttons.forEach((button) => {
      button.disabled = false;

      button.addEventListener('click', (event) => {
        if (!triggerSupplyUpgrade(this, button, 'direct')) return;

        event.preventDefault();
        event.stopPropagation();
      });
    });

    logSupplyUpgrade('buttons scan', {
      count: buttons.length,
      hasBanner: Boolean(this.querySelector('.cart-drawer-v2__supply-upgrade')),
      bound: true,
    });
  }

  setSupplyUpgradeLoading(trigger, isLoading) {
    if (!trigger?.isConnected) return;

    trigger.classList.toggle('is-loading', isLoading);
    trigger.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    trigger.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  }

  resetSupplyUpgradeButtons() {
    this.querySelectorAll(
      '[data-cart-drawer-v2-supply-upgrade], .cart-drawer-v2__supply-upgrade-button',
    ).forEach((button) => {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.setAttribute('aria-busy', 'false');
      button.setAttribute('aria-disabled', 'false');
      delete button.dataset.supplyUpgradeProcessing;
    });
  }

  async upgradeSubscriptionSupply(options = {}) {
    const {
      resolvedSourceLineKey,
      sourceVariantId,
      sourceSellingPlanId,
      targetVariantId,
      targetSellingPlanId,
      targetSellingPlanIds = [],
      quantity,
      sourceRestoreItem,
    } = options;

    let workingCart = await window.CartActionsV2.get();
    const sourceWasPresent = Boolean(
      this.findActiveSourceLine(workingCart, {
        sourceLineKey: resolvedSourceLineKey,
        sourceVariantId,
        sourceSellingPlanId,
      }),
    );

    logSupplyUpgrade('subscription swap start', {
      resolvedSourceLineKey,
      sourceWasPresent,
      sourceVariantId,
      sourceSellingPlanId,
      targetVariantId,
      targetSellingPlanId,
      targetSellingPlanIds,
    });

    const addTargetOptions = {
      productId: sourceRestoreItem?.product_id || '',
      properties: this.sanitizeSupplyUpgradeAddProperties(sourceRestoreItem?.properties),
      planCandidates: targetSellingPlanIds,
      excludeLineKey: resolvedSourceLineKey,
    };

    let effectiveTargetSellingPlanId = targetSellingPlanId;
    let addedTargetLineKey = '';

    try {
      // Add before removing: if Shopify rejects the subscription add, the customer keeps the
      // subscription they already had instead of silently losing it to a failed swap.
      const addedLine = await this.addSupplyUpgradeTargetLine(
        targetVariantId,
        quantity,
        targetSellingPlanId,
        addTargetOptions,
      );

      effectiveTargetSellingPlanId = this.getCartItemSellingPlanId(addedLine) || targetSellingPlanId;
      addedTargetLineKey = addedLine?.key || '';

      workingCart = await window.CartActionsV2.get();
      logSupplyUpgrade('target added', {
        requestedPlanId: targetSellingPlanId || null,
        effectivePlanId: effectiveTargetSellingPlanId || null,
        items: summarizeCartItemsForLog(workingCart),
      });

      if (
        !this.hasStrictSupplyUpgradeTarget(workingCart, targetVariantId, effectiveTargetSellingPlanId)
      ) {
        throw new Error('Supply upgrade target line missing after subscription add.');
      }

      workingCart = await this.ensureSupplyUpgradeSourceRemoved({
        cart: workingCart,
        sourceLineKey: resolvedSourceLineKey,
        sourceVariantId,
        sourceSellingPlanId,
      });

      workingCart = await window.CartActionsV2.get();
      logSupplyUpgrade('after source remove', { items: summarizeCartItemsForLog(workingCart) });

      try {
        await this.removeAllLeftoverSourceLines(sourceVariantId, sourceSellingPlanId);
        logSupplyUpgrade('leftover source removed', {
          items: summarizeCartItemsForLog(await window.CartActionsV2.get()),
        });
      } catch (leftoverError) {
        logSupplyUpgrade('leftover cleanup skipped', {
          reason: leftoverError?.message || String(leftoverError),
        });
      }

      workingCart = await window.CartActionsV2.get();
    } catch (error) {
      // The add succeeded but the source could not be removed: undo the add so the customer is
      // not left paying for both supplies.
      if (addedTargetLineKey) {
        const cartAfterFailure = await window.CartActionsV2.get();
        const sourceStillPresent = Boolean(
          this.findActiveSourceLine(cartAfterFailure, { sourceVariantId, sourceSellingPlanId }),
        );

        if (
          sourceStillPresent
          && this.hasStrictSupplyUpgradeTarget(
            cartAfterFailure,
            targetVariantId,
            effectiveTargetSellingPlanId,
          )
        ) {
          try {
            await window.CartActionsV2.change(addedTargetLineKey, 0, { dispatch: false });
            logSupplyUpgrade('target line rolled back', { key: addedTargetLineKey });
          } catch (rollbackError) {
            logSupplyUpgradeError('unable to roll back added target line', rollbackError, {
              lineKey: addedTargetLineKey,
            });
          }
        }
      }

      workingCart = await this.restoreSupplyUpgradeIfNeeded(await window.CartActionsV2.get(), {
        sourceVariantId,
        sourceSellingPlanId,
        targetVariantId,
        targetSellingPlanId: effectiveTargetSellingPlanId,
        sourceRestoreItem,
        error,
      });

      throw error;
    }

    if (
      !this.hasStrictSupplyUpgradeTarget(workingCart, targetVariantId, effectiveTargetSellingPlanId)
    ) {
      throw new Error('Supply upgrade target line missing after subscription swap.');
    }

    return {
      cart: workingCart,
      removedSourceForSubscription: sourceWasPresent,
      effectiveTargetSellingPlanId,
    };
  }

  assertSupplyUpgradeDidNotMergeSource(cart, options = {}) {
    const sourceVariantId = String(options.sourceVariantId || '');
    const sourceSellingPlanId = String(options.sourceSellingPlanId || '');
    const sourceQuantityBefore = options.sourceQuantityBefore || 0;
    const targetVariantId = String(options.targetVariantId || '');
    const hasTargetVariant = (cart?.items || []).some(
      (item) => String(item.variant_id || item.id || '') === targetVariantId,
    );

    const mergedSourceLine = (cart?.items || []).find((item) => {
      if (String(item.variant_id || item.id || '') !== sourceVariantId) return false;
      if (sourceSellingPlanId && this.getCartItemSellingPlanId(item) !== sourceSellingPlanId) return false;

      return item.quantity > sourceQuantityBefore;
    });

    if (mergedSourceLine && !hasTargetVariant) {
      throw new Error('Supply upgrade merged with the source line instead of replacing it.');
    }
  }

  async revertMergedSupplyUpgradeSource(cart, options = {}) {
    const sourceVariantId = String(options.sourceVariantId || '');
    const sourceSellingPlanId = String(options.sourceSellingPlanId || '');
    const sourceQuantityBefore = options.sourceQuantityBefore || 0;

    const mergedSourceLine = (cart?.items || []).find((item) => {
      if (String(item.variant_id || item.id || '') !== sourceVariantId) return false;
      if (sourceSellingPlanId && this.getCartItemSellingPlanId(item) !== sourceSellingPlanId) return false;

      return item.quantity > sourceQuantityBefore;
    });

    if (!mergedSourceLine) return cart;

    return window.CartActionsV2.change(mergedSourceLine.key, sourceQuantityBefore, {
      dispatch: false,
      sellingPlan: sourceSellingPlanId || '',
    });
  }

  findTargetUpgradeLine(cart, targetVariantId, targetSellingPlanId = '', excludeLineKey = '', options = {}) {
    const items = cart?.items || [];
    const normalizedTargetVariantId = String(targetVariantId || '');
    const normalizedTargetPlanId = this.normalizeSupplyUpgradeId(targetSellingPlanId);
    const normalizedProductId = String(options.productId || '');

    let matches = items.filter((item) => {
      if (!item?.quantity) return false;
      if (excludeLineKey && item.key === excludeLineKey) return false;

      return String(item.variant_id || item.id || '') === normalizedTargetVariantId;
    });

    if (!matches.length && normalizedProductId) {
      matches = items.filter((item) => {
        if (!item?.quantity) return false;
        if (excludeLineKey && item.key === excludeLineKey) return false;

        return String(item.product_id || '') === normalizedProductId;
      });
    }

    if (!matches.length) return null;

    if (normalizedTargetPlanId) {
      const planMatch = matches.find(
        (item) => this.getCartItemSellingPlanId(item) === normalizedTargetPlanId,
      );
      return planMatch || null;
    }

    return matches[matches.length - 1];
  }

  async ensureSupplyUpgradeTargetLine(cart, targetVariantId, targetSellingPlanId = '', quantity = 1, options = {}) {
    const lookupOptions = { productId: options.productId || '' };
    let targetLine = this.findTargetUpgradeLine(cart, targetVariantId, targetSellingPlanId, '', lookupOptions);

    if (targetLine) {
      logSupplyUpgrade('ensure target line OK', {
        key: targetLine.key,
        variant_id: targetLine.variant_id,
        selling_plan_id: this.getCartItemSellingPlanId(targetLine),
        selling_plan_name: targetLine.selling_plan_allocation?.selling_plan?.name || null,
      });
      return targetLine;
    }

    targetLine = this.findTargetUpgradeLine(cart, targetVariantId, '', '', lookupOptions);

    if (!targetLine || !targetSellingPlanId) {
      logSupplyUpgrade('ensure target line missing', {
        targetVariantId,
        targetSellingPlanId: targetSellingPlanId || null,
        productId: lookupOptions.productId || null,
        cartItems: summarizeCartItemsForLog(cart),
      });
      return targetLine;
    }

    logSupplyUpgradeApi('ensure target line apply selling_plan', {
      lineKey: targetLine.key,
      quantity,
      sellingPlanId: this.normalizeSupplyUpgradeId(targetSellingPlanId),
      beforePlanId: this.getCartItemSellingPlanId(targetLine),
    });

    await window.CartActionsV2.change(targetLine.key, quantity, {
      dispatch: false,
      sellingPlan: this.normalizeSupplyUpgradeId(targetSellingPlanId),
    });

    const nextCart = await window.CartActionsV2.get();
    const verifiedLine = this.findTargetUpgradeLine(
      nextCart,
      targetVariantId,
      targetSellingPlanId,
      '',
      lookupOptions,
    );

    logSupplyUpgrade('ensure target line after change', {
      found: Boolean(verifiedLine),
      matchedLine: verifiedLine
        ? {
            key: verifiedLine.key,
            selling_plan_id: this.getCartItemSellingPlanId(verifiedLine),
            selling_plan_name: verifiedLine.selling_plan_allocation?.selling_plan?.name || null,
          }
        : null,
      cartItems: summarizeCartItemsForLog(nextCart),
    });

    return verifiedLine;
  }

  resolveSupplyUpgradeSource(cart, options = {}) {
    const lineKey = this.findSourceLineKey(cart, options);
    if (!lineKey) return null;

    const sourceItem = (cart?.items || []).find((item) => item.key === lineKey);
    if (!sourceItem) return null;

    return {
      lineKey,
      sourceVariantId: String(sourceItem.variant_id || sourceItem.id || options.sourceVariantId || ''),
      sourceSellingPlanId: this.getCartItemSellingPlanId(sourceItem) || String(options.sourceSellingPlanId || ''),
    };
  }

  itemMatchesSourceSellingPlan(item, sourceSellingPlanId) {
    const normalizedSourcePlanId = this.normalizeSupplyUpgradeId(sourceSellingPlanId || '');
    if (!normalizedSourcePlanId) return true;

    return this.getCartItemSellingPlanId(item) === normalizedSourcePlanId;
  }

  itemMatchesSourceVariant(item, sourceVariantId, targetVariantId) {
    const itemVariantId = String(item.variant_id || item.id || '');
    if (!itemVariantId || itemVariantId !== sourceVariantId) return false;
    if (targetVariantId && itemVariantId === targetVariantId) return false;

    return true;
  }

  async upgradeLineSupply(trigger) {
    if (trigger?.dataset?.supplyUpgradeProcessing === 'true') {
      logSupplyUpgrade('aborted', { reason: 'upgrade already in progress' });
      return;
    }

    trigger.dataset.supplyUpgradeProcessing = 'true';

    const payload = this.getSupplyUpgradePayload(trigger);
    const sourceLineKey = payload.sourceLineKey || '';
    const sourceVariantId = String(payload.sourceVariantId || '');
    const targetVariantId = String(payload.targetVariantId || '');
    const targetSellingPlanId = String(payload.targetSellingPlanId || '');
    const targetSellingPlanIds = payload.targetSellingPlanIds || [];
    let effectiveTargetSellingPlanId = targetSellingPlanId;
    const parsedQuantity = Number.parseInt(String(payload.quantity ?? ''), 10);
    const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;

    logSupplyUpgrade('click', {
      sourceLineKey,
      sourceVariantId,
      sourceSellingPlanId: payload.sourceSellingPlanId || null,
      targetVariantId,
      targetSellingPlanId: targetSellingPlanId || null,
      targetSellingPlanIds,
      quantity,
    });

    if (!sourceLineKey || !sourceVariantId || !targetVariantId || trigger.getAttribute('aria-disabled') === 'true') {
      logSupplyUpgrade('aborted', {
        reason: 'missing payload or button disabled',
        sourceLineKey,
        sourceVariantId,
        targetVariantId,
        ariaDisabled: trigger.getAttribute('aria-disabled'),
      });
      delete trigger.dataset.supplyUpgradeProcessing;
      return;
    }

    if (sourceVariantId === targetVariantId) {
      logSupplyUpgrade('aborted', { reason: 'target variant matches source variant', sourceVariantId });
      delete trigger.dataset.supplyUpgradeProcessing;
      return;
    }

    let cart = await window.CartActionsV2.get();
    logSupplyUpgrade('cart loaded', { items: summarizeCartItemsForLog(cart) });
    const sourceContext = this.resolveSupplyUpgradeSource(cart, {
      fallbackLineKey: sourceLineKey,
      properties: this.getLineProperties(trigger),
      sourceSellingPlanId: String(payload.sourceSellingPlanId || ''),
      sourceVariantId,
      targetVariantId,
    });

    if (!sourceContext?.lineKey) {
      logSupplyUpgrade('failed', {
        reason: 'source line not found',
        items: summarizeCartItemsForLog(cart),
      });
      delete trigger.dataset.supplyUpgradeProcessing;
      await this.refresh();
      return;
    }

    const resolvedSourceLineKey =
      this.findActiveSourceLine(cart, {
        sourceLineKey: sourceContext.lineKey,
        sourceVariantId,
        sourceSellingPlanId: String(payload.sourceSellingPlanId || ''),
      })?.key || sourceContext.lineKey;
    const sourceItem = (cart?.items || []).find((item) => item.key === resolvedSourceLineKey);

    if (!sourceItem) {
      logSupplyUpgrade('failed', {
        reason: 'source line disappeared before upgrade',
        resolvedSourceLineKey,
        items: summarizeCartItemsForLog(cart),
      });
      delete trigger.dataset.supplyUpgradeProcessing;
      await this.refresh();
      return;
    }

    const sourceSellingPlanId = this.getCartItemSellingPlanId(sourceItem);
    const sourceHasSubscription = Boolean(sourceSellingPlanId);

    logSupplyUpgrade('source resolved', {
      resolvedSourceLineKey,
      sourceVariantId,
      sourceSellingPlanId: sourceSellingPlanId || null,
      sourceHasSubscription,
      buttonLineKey: sourceLineKey,
      lineKeyChanged: sourceLineKey !== resolvedSourceLineKey,
    });
    const sourceQuantityBefore = sourceItem.quantity;
    const sourceRestoreItem = this.buildSupplyUpgradeRestoreItem(sourceItem);
    const mergeGuardOptions = {
      sourceVariantId,
      sourceSellingPlanId,
      sourceQuantityBefore,
      targetVariantId,
    };

    if (sourceHasSubscription && !targetSellingPlanId && !targetSellingPlanIds.length) {
      logSupplyUpgrade('failed', {
        reason: 'target selling plan id missing on upgrade button',
        sourceHasSubscription,
      });
      delete trigger.dataset.supplyUpgradeProcessing;
      return;
    }

    this.setLineControlsDisabled(resolvedSourceLineKey, true);
    this.setLineItemLoading(resolvedSourceLineKey, true);
    this.setSupplyUpgradeLoading(trigger, true);

    const sellingPlanBefore = summarizeCartItemsForLog(cart);
    supplyUpgradeDebugActive = true;
    logSupplyUpgrade('selling plan BEFORE', { items: sellingPlanBefore });

    let removedSourceForSubscription = false;

    try {
      if (sourceHasSubscription) {
        const subscriptionUpgradeResult = await this.upgradeSubscriptionSupply({
          cart,
          resolvedSourceLineKey,
          sourceVariantId,
          sourceSellingPlanId,
          targetVariantId,
          targetSellingPlanId,
          targetSellingPlanIds,
          quantity,
          sourceRestoreItem,
        });

        cart = subscriptionUpgradeResult.cart;
        effectiveTargetSellingPlanId =
          subscriptionUpgradeResult.effectiveTargetSellingPlanId || targetSellingPlanId;
        removedSourceForSubscription = subscriptionUpgradeResult.removedSourceForSubscription;
        this.assertSupplyUpgradeDidNotMergeSource(cart, mergeGuardOptions);
      } else {
        await this.addSupplyUpgradeTargetLine(targetVariantId, quantity, targetSellingPlanId, {
          productId: sourceItem.product_id || '',
          excludeLineKey: resolvedSourceLineKey,
        });

        cart = await window.CartActionsV2.get();
        this.assertSupplyUpgradeDidNotMergeSource(cart, mergeGuardOptions);

        const targetLine = this.findTargetUpgradeLine(
          cart,
          targetVariantId,
          targetSellingPlanId,
          resolvedSourceLineKey,
          { productId: sourceItem.product_id || '' },
        );

        if (!targetLine) {
          console.error('Unable to upgrade cart item supply: target line was not added to cart.');
          return;
        }

        const sourceLineStillInCart = (cart?.items || []).find((item) => item.key === resolvedSourceLineKey);

        if (sourceLineStillInCart && sourceLineStillInCart.key !== targetLine.key) {
          cart = await this.removeSupplyUpgradeSourceLine(
            cart,
            resolvedSourceLineKey,
            sourceSellingPlanId,
          );
          cart = await this.ensureSupplyUpgradeSourceRemoved({
            sourceLineKey: resolvedSourceLineKey,
            sourceVariantId,
            sourceSellingPlanId,
          });
        }
      }

      cart = await window.CartActionsV2.get();
      const targetLine = await this.ensureSupplyUpgradeTargetLine(
        cart,
        targetVariantId,
        effectiveTargetSellingPlanId,
        quantity,
        { productId: sourceItem.product_id || '' },
      );

      if (!targetLine) {
        await this.restoreSupplyUpgradeIfNeeded(cart, {
          sourceVariantId,
          sourceSellingPlanId,
          targetVariantId,
          targetSellingPlanId: effectiveTargetSellingPlanId,
          sourceRestoreItem,
        });

        console.error(
          'Unable to upgrade cart item supply: target line was not added to cart.',
          targetVariantId,
          effectiveTargetSellingPlanId,
        );
        return;
      }

      if (window.SubscriptionBonusProducts?.syncCart) {
        cart = await window.SubscriptionBonusProducts.syncCart(cart, {
          showLoading: false,
          skipOrderFix: true,
        });
      }

      dispatchHeaderCartCount(cart);
      const sellingPlanAfter = summarizeCartItemsForLog(cart);
      logSupplyUpgrade('selling plan AFTER', { items: sellingPlanAfter });
      logSupplyUpgrade('success', {
        expected: {
          variant_id: targetVariantId,
          selling_plan_id: effectiveTargetSellingPlanId || null,
        },
        items: sellingPlanAfter,
      });
    } catch (error) {
      try {
        cart = await window.CartActionsV2.get();
        cart = await this.revertMergedSupplyUpgradeSource(cart, mergeGuardOptions);
      } catch (revertError) {
        console.error('Unable to revert merged supply upgrade source line', revertError);
      }

      cart = await this.restoreSupplyUpgradeIfNeeded(cart, {
        sourceVariantId,
        sourceSellingPlanId,
        targetVariantId,
        targetSellingPlanId: effectiveTargetSellingPlanId,
        sourceRestoreItem,
        error,
      });

      cart = await window.CartActionsV2.get();
      logSupplyUpgrade('failed', {
        reason: error?.message || String(error),
        items: summarizeCartItemsForLog(cart),
      });
    } finally {
      supplyUpgradeDebugActive = false;
      this.setLineControlsDisabled(resolvedSourceLineKey, false);
      this.setLineItemLoading(resolvedSourceLineKey, false);
      this.setSupplyUpgradeLoading(trigger, false);
      delete trigger.dataset.supplyUpgradeProcessing;

      await this.refresh();
    }
  }

  findSourceLineKey(cart, options = {}) {
    const sourceVariantId = String(options.sourceVariantId || '');
    const sourceSellingPlanId = this.normalizeSupplyUpgradeId(options.sourceSellingPlanId || '');
    const targetVariantId = String(options.targetVariantId || '');
    const sourceProperties = this.normalizeLineProperties(options.properties);
    const fallbackLineKey = options.fallbackLineKey || '';
    const items = cart?.items || [];

    const matchesSourceLine = (item, requirePropertyMatch) => {
      if (!this.itemMatchesSourceVariant(item, sourceVariantId, targetVariantId)) return false;
      if (!this.itemMatchesSourceSellingPlan(item, sourceSellingPlanId)) return false;

      if (!requirePropertyMatch) return true;

      return this.linePropertiesMatch(this.normalizeLineProperties(item.properties), sourceProperties);
    };

    if (sourceVariantId && sourceSellingPlanId) {
      const subscriptionMatch = items.find((item) => matchesSourceLine(item, false));
      if (subscriptionMatch?.key) return subscriptionMatch.key;
    }

    if (sourceVariantId) {
      const subscriptionLines = items.filter((item) => {
        if (!this.getCartItemSellingPlanId(item)) return false;

        return this.itemMatchesSourceVariant(item, sourceVariantId, targetVariantId);
      });

      if (subscriptionLines.length === 1 && subscriptionLines[0]?.key) {
        return subscriptionLines[0].key;
      }
    }

    const keyMatch = items.find((item) => item.key === fallbackLineKey);
    if (keyMatch?.key && this.itemMatchesSourceVariant(keyMatch, sourceVariantId, targetVariantId)) {
      if (!sourceSellingPlanId || this.getCartItemSellingPlanId(keyMatch) === sourceSellingPlanId) {
        return fallbackLineKey;
      }
    }

    const strictMatch = items.find((item) => matchesSourceLine(item, true));
    if (strictMatch?.key) return strictMatch.key;

    const looseMatch = items.find((item) => matchesSourceLine(item, false));
    if (looseMatch?.key) return looseMatch.key;

    return '';
  }

  getCartItemSellingPlanId(item) {
    return this.normalizeSupplyUpgradeId(
      item?.selling_plan_allocation?.selling_plan?.id
        || item?.selling_plan_allocation?.selling_plan_id
        || item?.selling_plan
        || '',
    );
  }

  sanitizeSupplyUpgradeAddProperties(properties) {
    return Object.fromEntries(
      Object.entries(this.normalizeLineProperties(properties)).filter(
        ([key]) => !String(key).startsWith('_'),
      ),
    );
  }

  hasStrictSupplyUpgradeTarget(cart, targetVariantId, targetSellingPlanId = '') {
    return Boolean(
      this.findTargetUpgradeLine(cart, targetVariantId, targetSellingPlanId, '', { productId: '' }),
    );
  }

  async restoreSupplyUpgradeIfNeeded(cart, options = {}) {
    const {
      sourceVariantId,
      sourceSellingPlanId,
      targetVariantId,
      targetSellingPlanId,
      sourceRestoreItem,
      error = null,
    } = options;

    if (error?.supplyUpgradeRestored) return cart;

    const workingCart = cart || (await window.CartActionsV2.get());
    const hasTarget = this.hasStrictSupplyUpgradeTarget(
      workingCart,
      targetVariantId,
      targetSellingPlanId,
    );
    const sourceReturned = Boolean(
      this.findActiveSourceLine(workingCart, { sourceVariantId, sourceSellingPlanId }),
    );

    if (!hasTarget && !sourceReturned && sourceRestoreItem) {
      try {
        await this.restoreSupplyUpgradeSourceLine(sourceRestoreItem);
        if (error) {
          error.supplyUpgradeRestored = true;
        }
      } catch (restoreError) {
        console.error('Unable to restore source line after failed supply upgrade', restoreError);
      }
    }

    return window.CartActionsV2.get();
  }

  normalizeLineProperties(properties) {
    if (!properties) return {};

    if (Array.isArray(properties)) {
      return properties.reduce((normalizedProperties, property) => {
        if (!Array.isArray(property) || property.length < 2) return normalizedProperties;
        if (property[1] === null || property[1] === '') return normalizedProperties;

        normalizedProperties[property[0]] = String(property[1]);
        return normalizedProperties;
      }, {});
    }

    if (typeof properties === 'object') {
      return Object.entries(properties).reduce((normalizedProperties, [key, value]) => {
        if (value === null || value === '') return normalizedProperties;

        normalizedProperties[key] = String(value);
        return normalizedProperties;
      }, {});
    }

    return {};
  }

  linePropertiesMatch(currentProperties, sourceProperties) {
    const currentEntries = Object.entries(currentProperties);
    const sourceEntries = Object.entries(sourceProperties);

    if (currentEntries.length !== sourceEntries.length) return false;

    return sourceEntries.every(([key, value]) => currentProperties[key] === value);
  }

  getLineProperties(trigger) {
    const rawProperties = trigger?.getAttribute('data-line-properties') || '';

    if (!rawProperties) return {};

    try {
      const parsedProperties = JSON.parse(rawProperties);

      if (Array.isArray(parsedProperties)) {
        return parsedProperties.reduce((properties, property) => {
          if (!Array.isArray(property) || property.length < 2) return properties;
          if (property[1] === null || property[1] === '') return properties;

          properties[property[0]] = property[1];
          return properties;
        }, {});
      }

      if (parsedProperties && typeof parsedProperties === 'object') {
        return Object.entries(parsedProperties).reduce((properties, [key, value]) => {
          if (value === null || value === '') return properties;

          properties[key] = value;
          return properties;
        }, {});
      }
    } catch (error) {
      return {};
    }

    return {};
  }

  setLineItemLoading(lineKey, isLoading) {
    const lineItem = this.getLineItem(lineKey);

    if (!lineItem) return;

    lineItem.classList.toggle('is-loading', isLoading);
    lineItem.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  setLineControlsDisabled(lineKey, isDisabled) {
    const lineItem = this.getLineItem(lineKey);

    if (!lineItem) return;

    lineItem.querySelectorAll('button, a, select').forEach((control) => {
      control.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

      if (control.tagName === 'BUTTON' || control.tagName === 'SELECT') {
        control.disabled = isDisabled;
      }
    });
  }

  getLineItem(lineKey) {
    return [...this.querySelectorAll('[data-cart-drawer-v2-item]')].find((lineItem) => lineItem.dataset.lineKey === lineKey);
  }

  async applyDiscountCode(trigger) {
    const form = trigger.closest('[data-cart-drawer-v2-form]');
    const input = form?.querySelector('[data-cart-drawer-v2-discount-input]');
    const discountCode = input?.value.trim() || '';

    if (!input) return;

    this.clearDiscountMessages(form);

    if (!discountCode) {
      this.saveDiscountCode('');
      this.showDiscountError(form);
      return;
    }

    trigger.disabled = true;
    trigger.classList.add('is-loading');
    input.setAttribute('aria-busy', 'true');

    try {
      const previousCodes = this.mergeDiscountCodes([...this.getSavedDiscountCodes(), ...this.getRenderedDiscountCodes()]);
      const requestedCodes = this.mergeDiscountCodes([...previousCodes, discountCode]);
      const cart = await window.CartActionsV2.updateDiscounts(requestedCodes);

      if (!this.isDiscountCodeAccepted(cart, discountCode)) {
        const restoredCart = await window.CartActionsV2.updateDiscounts(previousCodes);

        this.saveDiscountCodes(previousCodes);
        await this.refresh();
        this.hydrateDiscountField();
        this.hydrateDiscountList(previousCodes, restoredCart);
        this.updateSubtotalFromCart(restoredCart);
        this.showDiscountError(this.querySelector('[data-cart-drawer-v2-form]'));
        return;
      }

      const activeCodes = this.getDiscountCodesFromCart(cart);
      const discountCodes = activeCodes.length > 0 ? activeCodes : requestedCodes;

      this.saveDiscountCodes(discountCodes);
      await this.refresh();
      this.hydrateDiscountField();
      this.hydrateDiscountList(discountCodes, cart);
      this.updateLinePricesFromCart(cart);
      this.updateSubtotalFromCart(cart);
      this.celebrateSubtotal();
    } catch (error) {
      console.error('Unable to apply discount code', error);
      this.showDiscountError(form);
    } finally {
      trigger.disabled = false;
      trigger.classList.remove('is-loading');
      input.removeAttribute('aria-busy');
    }
  }

  async removeDiscountCode(trigger) {
    const form = trigger.closest('[data-cart-drawer-v2-form]');
    const discountCode = trigger.dataset.discountCode || '';
    const input = form?.querySelector('[data-cart-drawer-v2-discount-input]');

    if (!discountCode || trigger.getAttribute('aria-disabled') === 'true') return;

    this.clearDiscountMessages(form);
    trigger.disabled = true;
    trigger.setAttribute('aria-disabled', 'true');
    trigger.closest('[data-cart-drawer-v2-discount-item]')?.classList.add('is-loading');

    try {
      const discountCodes = this.mergeDiscountCodes([...this.getSavedDiscountCodes(), ...this.getRenderedDiscountCodes()]).filter(
        (code) => this.normalizeDiscountCode(code) !== this.normalizeDiscountCode(discountCode),
      );
      const cart = await window.CartActionsV2.updateDiscounts(discountCodes);
      const activeCodes = this.getDiscountCodesFromCart(cart);

      this.saveDiscountCodes(activeCodes.length > 0 ? activeCodes : discountCodes);
      await this.refresh();
      this.hydrateDiscountField();
      this.hydrateDiscountList(activeCodes.length > 0 ? activeCodes : discountCodes, cart);
      this.updateLinePricesFromCart(cart);
      this.updateSubtotalFromCart(cart);
      this.celebrateSubtotal();

      if (input && this.normalizeDiscountCode(input.value) === this.normalizeDiscountCode(discountCode)) {
        input.value = '';
      }
    } catch (error) {
      console.error('Unable to remove discount code', error);
      this.showDiscountError(form);
    } finally {
      trigger.disabled = false;
      trigger.setAttribute('aria-disabled', 'false');
      trigger.closest('[data-cart-drawer-v2-discount-item]')?.classList.remove('is-loading');
    }
  }

  showDiscountError(form) {
    const input = form?.querySelector('[data-cart-drawer-v2-discount-input]');
    const error = form?.querySelector('[data-cart-drawer-v2-discount-error]');

    input?.classList.add('is-error');
    input?.setAttribute('aria-invalid', 'true');
    error?.removeAttribute('hidden');
  }

  clearDiscountMessages(form) {
    const input = form?.querySelector('[data-cart-drawer-v2-discount-input]');
    const error = form?.querySelector('[data-cart-drawer-v2-discount-error]');

    input?.classList.remove('is-error');
    input?.removeAttribute('aria-invalid');
    error?.setAttribute('hidden', '');
  }

  hydrateDiscountField() {
    const input = this.querySelector('[data-cart-drawer-v2-discount-input]');

    if (!input) return;

    input.value = '';
  }

  syncDiscountCodesFromCart(cart) {
    const codes = this.mergeDiscountCodes([
      ...this.getSavedDiscountCodes(),
      ...this.getRenderedDiscountCodes(),
      ...this.getDiscountCodesFromCart(cart),
    ]);

    if (codes.length === 0) return;

    this.saveDiscountCodes(codes);
    this.hydrateDiscountList(codes, cart);
  }

  async syncDiscountedPricesFromCart() {
    if (!window.CartActionsV2?.get) return;

    try {
      let cart = await window.CartActionsV2.get();
      const savedCodes = this.getSavedDiscountCodes();
      const cartCodes = this.getDiscountCodesFromCart(cart);
      const hasMissingSavedCode = savedCodes.some((code) => {
        const normalizedCode = this.normalizeDiscountCode(code);
        return !cartCodes.some((cartCode) => this.normalizeDiscountCode(cartCode) === normalizedCode);
      });

      if (hasMissingSavedCode && typeof window.CartActionsV2.updateDiscounts === 'function') {
        cart = await window.CartActionsV2.updateDiscounts(this.mergeDiscountCodes([...cartCodes, ...savedCodes]));
        await this.refresh();
      }

      this.hydrateDiscountField();
      this.hydrateDiscountList(this.mergeDiscountCodes([...savedCodes, ...this.getDiscountCodesFromCart(cart)]), cart);
      this.updateLinePricesFromCart(cart);
      this.updateSubtotalFromCart(cart);
    } catch (error) {
      console.error('Unable to sync discounted cart prices', error);
    }
  }

  hydrateDiscountList(discountCodes = this.getSavedDiscountCodes(), cart = null) {
    const list = this.querySelector('[data-cart-drawer-v2-discount-list]');
    if (!list) return;

    const discounts = this.mergeDiscountDetails([
      ...this.getDiscountDetailsFromCart(cart),
      ...(cart ? [] : this.getRenderedDiscountCodes().map((code) => ({ code }))),
      ...discountCodes.map((code) => ({ code })),
    ]);

    list.innerHTML = discounts.map((discount) => this.renderDiscountItem(discount)).join('');
    list.hidden = discounts.length === 0;

    const codes = discounts.map((discount) => discount.code);
    this.syncCheckoutDiscountField(codes);

    if (codes.length > 0) {
      this.saveDiscountCodes(codes);
    }
  }

  syncCheckoutDiscountField(discountCodes = this.getRenderedDiscountCodes()) {
    const input = this.querySelector('[data-cart-drawer-v2-discount-checkout-input]');

    if (!input) return;

    input.value = this.mergeDiscountCodes(discountCodes).join(',');
  }

  renderDiscountItem(discount) {
    const code = this.escapeHtml(discount.code);
    const amount = Number.isFinite(discount.amount) && discount.amount > 0 ? `<span class="cart-drawer-v2__discount-amount">-${this.formatCartMoney(discount.amount)}</span>` : '';
    const removeLabel = `Remove ${discount.code}`;

    return `
      <li class="cart-drawer-v2__discount-item" data-cart-drawer-v2-discount-item data-discount-code="${code}">
        <svg aria-hidden="true" focusable="false" class="cart-drawer-v2__discount-icon" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.304.79h8.571l12 11.835M9.594 4.17H2.589c-.946 0-1.714.758-1.714 1.692v6.907c0 .449.18.88.502 1.196l10.47 10.329c.67.66 1.755.66 2.423 0 .173-.17.308-.376.394-.602l1.64-4.304 4.364-1.615a1.684 1.684 0 0 0 .61-2.779L10.805 4.666a1.725 1.725 0 0 0-1.211-.495Z" stroke="currentColor" stroke-width=".875" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.018 8.82a.426.426 0 0 0-.429.423c0 .234.192.423.429.423.236 0 .428-.19.428-.423a.424.424 0 0 0-.426-.422h-.002Z" stroke="currentColor" stroke-width=".875" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="cart-drawer-v2__discount-code">${code}</span>
        ${amount}
        <button
          class="cart-drawer-v2__discount-remove"
          type="button"
          aria-label="${this.escapeHtml(removeLabel)}"
          data-cart-drawer-v2-discount-remove
          data-discount-code="${code}"
        >
          <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            <path d="M10.5 3.5L3.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </li>
    `;
  }

  getRenderedDiscountCodes() {
    return [...this.querySelectorAll('[data-cart-drawer-v2-discount-item]')]
      .map((item) => item.dataset.discountCode || '')
      .filter(Boolean);
  }

  getDiscountCodesFromCart(cart) {
    return this.getDiscountDetailsFromCart(cart).map((discount) => discount.code);
  }

  getDiscountDetailsFromCart(cart) {
    if (!cart) return [];

    const discounts = [];
    const discountApplications = [...(cart.cart_level_discount_applications || []), ...(cart.discount_applications || [])];

    discountApplications.forEach((discount) => {
      if (discount.type && discount.type !== 'discount_code') return;

      const code = discount.code || discount.title;
      if (!code) return;

      discounts.push({
        code,
        amount: this.parseMoneySubunit(discount.total_allocated_amount),
      });
    });

    (cart.discount_codes || []).forEach((discount) => {
      const code = typeof discount === 'string' ? discount : discount?.code;

      if (!code) return;

      discounts.push({ code });
    });

    return this.mergeDiscountDetails(discounts);
  }

  isDiscountCodeAccepted(cart, discountCode) {
    if (!cart) return true;

    const normalizedCode = this.normalizeDiscountCode(discountCode);
    const matchingCartCode = (cart.discount_codes || []).find((discount) => this.normalizeDiscountCode(discount?.code || discount) === normalizedCode);

    if (matchingCartCode && matchingCartCode.applicable === false) return false;

    return true;
  }

  mergeDiscountDetails(discounts) {
    return discounts.reduce((mergedDiscounts, discount) => {
      const code = String(discount?.code || '').trim();
      const normalizedCode = this.normalizeDiscountCode(code);

      if (!code || mergedDiscounts.some((mergedDiscount) => this.normalizeDiscountCode(mergedDiscount.code) === normalizedCode)) {
        return mergedDiscounts;
      }

      mergedDiscounts.push({
        code,
        amount: this.parseMoneySubunit(discount.amount),
      });

      return mergedDiscounts;
    }, []);
  }

  mergeDiscountCodes(discountCodes) {
    return this.mergeDiscountDetails(discountCodes.map((code) => ({ code }))).map((discount) => discount.code);
  }

  parseMoneySubunit(value) {
    if (typeof value === 'string' && value.includes('.')) {
      const decimalAmount = Number.parseFloat(value);

      return Number.isFinite(decimalAmount) ? Math.round(decimalAmount * 100) : 0;
    }

    const amount = Number.parseInt(value, 10);

    return Number.isFinite(amount) ? amount : 0;
  }

  normalizeDiscountCode(value) {
    return String(value || '').trim().toLowerCase();
  }

  escapeHtml(value) {
    const element = document.createElement('textarea');
    element.textContent = String(value || '');

    return element.innerHTML;
  }

  getLineCompareLinePrice(item) {
    const quantity = Number.parseInt(item?.quantity, 10) || 0;
    if (!quantity) return 0;

    const variantPrice = Number.parseInt(item?.price ?? item?.original_price, 10) || 0;
    const originalLinePrice = Number.parseInt(item?.original_line_price, 10) || 0;
    const isBonus = Boolean(this.getBonusLineProperty(item));

    if (isBonus) {
      if (originalLinePrice > 0) return originalLinePrice;

      const finalLine = Number.parseInt(item?.final_line_price, 10) || 0;
      if (finalLine > 0) return finalLine;

      return variantPrice * quantity;
    }

    const allocation = item?.selling_plan_allocation;
    let compareUnit = variantPrice;

    if (allocation) {
      const allocationCompare = Number.parseInt(allocation.compare_at_price, 10);
      compareUnit = allocationCompare > 0 ? allocationCompare : variantPrice;
    } else {
      const variantCompareAt = Number.parseInt(item?.compare_at_price, 10) || 0;
      const finalUnitPrice = Number.parseInt(item?.final_price, 10) || 0;

      if (variantCompareAt > finalUnitPrice) {
        compareUnit = variantCompareAt;
      }
    }

    let compareLine = compareUnit * quantity;
    if (originalLinePrice > compareLine) {
      compareLine = originalLinePrice;
    }

    return compareLine;
  }

  getCartBonusTotal(cart) {
    return (cart?.items || []).reduce((sum, item) => {
      if (!this.getBonusLineProperty(item)) return sum;
      return sum + (Number.parseInt(item?.final_line_price, 10) || 0);
    }, 0);
  }

  getCartDisplayTotal(cart) {
    const bonusTotal = this.getCartBonusTotal(cart);
    const payableTotal = Number.parseInt(cart?.total_price, 10);

    if (Number.isFinite(payableTotal)) {
      return Math.max(0, payableTotal - bonusTotal);
    }

    return Math.max(
      0,
      (cart?.items || []).reduce((sum, item) => {
        if (this.getBonusLineProperty(item)) return sum;
        return sum + (Number.parseInt(item?.final_line_price, 10) || 0);
      }, 0),
    );
  }

  getCartCompareTotal(cart) {
    return (cart?.items || []).reduce(
      (sum, item) => sum + this.getLineCompareLinePrice(item),
      0
    );
  }

  updateSubtotalFromCart(cart) {
    const subtotal = this.querySelector('[data-cart-drawer-v2-subtotal]');
    const subtotalCompare = this.querySelector('[data-cart-drawer-v2-subtotal-compare]');
    const subtotalSavings = this.querySelector('[data-cart-drawer-v2-subtotal-savings]');
    const displayTotal = this.getCartDisplayTotal(cart);
    const compareTotal = this.getCartCompareTotal(cart);
    const savingsTotal = compareTotal - displayTotal;

    if (!subtotal) return;

    subtotal.dataset.cartSubtotalPrice = String(displayTotal);
    subtotal.innerHTML = this.formatCartMoney(
      displayTotal,
      !document.documentElement.classList.contains('ab-test-cart-redesign')
    );

    if (subtotalCompare) {
      if (compareTotal > displayTotal) {
        subtotalCompare.textContent = this.formatCartMoney(
          compareTotal,
          !document.documentElement.classList.contains('ab-test-cart-redesign')
        );
        subtotalCompare.removeAttribute('hidden');
        subtotalCompare.classList.remove('is-empty');
      } else {
        subtotalCompare.textContent = '';
        subtotalCompare.setAttribute('hidden', '');
        subtotalCompare.classList.add('is-empty');
      }
    }

    if (subtotalSavings) {
      if (savingsTotal > 0) {
        subtotalSavings.textContent = `${this.formatCartMoney(savingsTotal)} OFF`;
        subtotalSavings.removeAttribute('hidden');
        subtotalSavings.classList.remove('is-empty');
      } else {
        subtotalSavings.textContent = '';
        subtotalSavings.setAttribute('hidden', '');
        subtotalSavings.classList.add('is-empty');
      }
    }

    this.updateShippingValueFromCart(cart);
  }

  getLineDisplayPrice(item) {
    return Number.parseInt(item?.final_line_price, 10) || 0;
  }

  updateLinePricesFromCart(cart) {
    (cart?.items || []).forEach((item) => {
      const row = this.getLineItem(item.key);
      if (!row || row.hasAttribute('data-subscription-bonus')) return;

      const displayPrice = this.getLineDisplayPrice(item);
      const comparePrice = this.getLineCompareLinePrice(item);
      const current = row.querySelector('.cart-drawer-v2-item__price-current');
      const compare = row.querySelector('.cart-drawer-v2-item__price-compare');

      if (current) current.textContent = this.formatCartMoney(displayPrice);

      if (compare) {
        if (comparePrice > displayPrice) {
          compare.textContent = this.formatCartMoney(comparePrice);
        } else {
          compare.textContent = '';
        }
      }
    });
  }

  getBonusLineProperty(item, key = '_subscription_bonus_for') {
    if (window.SubscriptionBonusProducts?.getLineProperty) {
      return window.SubscriptionBonusProducts.getLineProperty(item, key);
    }

    const properties = item?.properties;
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return '';

    return properties[key] == null ? '' : String(properties[key]);
  }

  cartHasSubscription(cart) {
    return (cart?.items || []).some((item) => {
      if (this.getBonusLineProperty(item)) return false;

      return Boolean(
        item?.selling_plan_allocation?.selling_plan?.id
          || item?.selling_plan_allocation?.selling_plan_id
          || item?.selling_plan,
      );
    });
  }

  updateShippingValueFromCart(cart) {
    const shippingValue = this.querySelector('[data-cart-drawer-v2-shipping-value]');
    if (!shippingValue) return;

    const hasSubscription = this.cartHasSubscription(cart);
    const oneTimeValue = shippingValue.dataset.shippingOneTime || this.formatCartMoney(799);
    const subscriptionValue = shippingValue.dataset.shippingSubscription || 'FREE';

    shippingValue.dataset.shippingType = hasSubscription ? 'subscription' : 'one-time';
    shippingValue.classList.toggle('is-free', hasSubscription);

    if (hasSubscription) {
      shippingValue.innerHTML = `
        <s class="cart-drawer-v2__shipping-compare"></s>
        <span class="cart-drawer-v2__shipping-free"></span>
      `;
      shippingValue.querySelector('.cart-drawer-v2__shipping-compare').textContent = oneTimeValue;
      shippingValue.querySelector('.cart-drawer-v2__shipping-free').textContent = subscriptionValue;
      return;
    }

    shippingValue.textContent = oneTimeValue;
  }

  formatCartMoney(cents, withCurrency = false) {
    if (cents === 0) return window.theme?.strings?.free || 'Free';

    const moneyFormat = withCurrency
      ? window.theme?.moneyWithCurrencyFormat || window.theme?.moneyFormat
      : window.theme?.moneyFormat || window.theme?.moneyWithCurrencyFormat;

    if (typeof window.theme?.formatMoney === 'function' && moneyFormat) {
      return window.theme.formatMoney(cents, moneyFormat);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  celebrateSubtotal() {
    const subtotal = this.querySelector('[data-cart-drawer-v2-subtotal]');
    const totalRow = subtotal?.closest('.cart-drawer-v2__summary-row--total');

    if (!totalRow) return;

    window.clearTimeout(this.discountCelebrationTimer);
    totalRow.classList.remove('is-celebrating');
    void totalRow.offsetWidth;
    totalRow.classList.add('is-celebrating');

    this.discountCelebrationTimer = window.setTimeout(() => {
      totalRow.classList.remove('is-celebrating');
    }, 1200);
  }

  getSavedDiscountCode() {
    try {
      return window.sessionStorage.getItem(this.discountStorageKey) || '';
    } catch (error) {
      return '';
    }
  }

  getSavedDiscountCodes() {
    return this.mergeDiscountCodes(this.getSavedDiscountCode().split(','));
  }

  saveDiscountCode(discountCode) {
    this.saveDiscountCodes(discountCode ? [discountCode] : []);
  }

  saveDiscountCodes(discountCodes = []) {
    try {
      const codes = this.mergeDiscountCodes(discountCodes);

      if (codes.length > 0) {
        window.sessionStorage.setItem(this.discountStorageKey, codes.join(','));
        return;
      }

      window.sessionStorage.removeItem(this.discountStorageKey);
    } catch (error) {
      // Ignore storage access errors in private or restricted browser contexts.
    }
  }

  syncPolicyCheckoutState() {
    if (!this.policyCheckbox || !this.checkoutButton) return;

    this.checkoutButton.disabled = !this.policyCheckbox.checked;
  }

  async submitCheckout(trigger) {
    const form = trigger?.closest('form');
    if (!form) return;

    trigger.disabled = true;

    try {
      if (window.SubscriptionBonusProducts?.syncCart && window.CartActionsV2?.get) {
        const cart = await window.CartActionsV2.get();
        await window.SubscriptionBonusProducts.syncCart(cart, { skipOrderFix: true });
      }
    } catch (error) {
      console.error('Unable to clean subscription bonus products before checkout', error);
    }

    if (typeof form.requestSubmit === 'function') {
      trigger.disabled = false;
      form.requestSubmit(trigger);
      return;
    }

    form.submit();
  }

  async refresh() {
    try {
      const response = await fetch(this.sectionUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to refresh cart drawer.');
      }

      const html = await response.text();
      const nextDrawer = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer-v2');

      if (!nextDrawer) {
        throw new Error('Cart drawer v2 section was not found in response.');
      }

      this.renderCartDrawer(nextDrawer);
    } catch (error) {
      console.error('Unable to refresh cart drawer', error);
    }
  }

  renderCartDrawer(nextDrawer) {
    const shouldStayOpen = this.classList.contains('is-open');
    const lastTrigger = this.lastTrigger;

    this.innerHTML = nextDrawer.innerHTML;
    this.bindElements();

    const cartCountNode = this.querySelector('[data-cart-drawer-v2-count]');
    const cartCount = Number.parseInt(cartCountNode?.textContent || '', 10);
    if (Number.isFinite(cartCount)) {
      dispatchHeaderCartCount(cartCount);
    }

    if (shouldStayOpen) {
      this.open(lastTrigger);
    } else {
      this.close(false);
    }
  }

  get sectionUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'cart-drawer-v2');
    url.searchParams.set('_', String(Date.now()));

    return url.toString();
  }
}

const existingCartDrawerV2 = customElements.get('cart-drawer-v2');

if (!existingCartDrawerV2) {
  customElements.define('cart-drawer-v2', CartDrawerV2);
} else {
  Object.getOwnPropertyNames(CartDrawerV2.prototype).forEach((name) => {
    if (name === 'constructor') return;

    existingCartDrawerV2.prototype[name] = CartDrawerV2.prototype[name];
  });

  document.querySelectorAll('cart-drawer-v2').forEach((drawer) => {
    document.removeEventListener('click', drawer.onDocumentClick, true);
    drawer.onDocumentClick = existingCartDrawerV2.prototype.onDocumentClick.bind(drawer);
    document.addEventListener('click', drawer.onDocumentClick, true);
  });

  logSupplyUpgrade('custom element prototype patched', { build: CART_DRAWER_V2_BUILD });
}

window.__cartDrawerV2Build = CART_DRAWER_V2_BUILD;
console.log('[cart-drawer-v2] supply-upgrade → script loaded', { build: CART_DRAWER_V2_BUILD });
